/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useCallback, useState, useRef, useEffect, StrictMode } from "react";

import { ModuleRegistry, provideGlobalGridOptions } from "ag-grid-community"; // , AllCommunityModule, ValidationModule
import { AllEnterpriseModule, IntegratedChartsModule } from "ag-grid-enterprise"; // , LicenseManager
import { AgGridReact } from "ag-grid-react";
import { AgChartsEnterpriseModule } from "ag-charts-enterprise";
// import type {IRowNode} from "ag-grid-community";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// AGgrid Localization:
import AG_GRID_LOCALE_EN from "../../localization/aggrid-en"
import AG_GRID_LOCALE_RU from "../../localization/aggrid-ru"

// My Components:
import SelectForAGgrid from "../react/SelectForAGgrid.tsx";
import AutocompleteForAGgrid from "../react/AutocompleteForAGgrid.tsx";
import CascaderForAGgrid from "../react/CascaderForAGgrid.tsx";
// import DivE from "../react/DivE.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";
// Reducers:
import { getLng, getDict, getTheme, getCompact, getDesktopType, getAppState, changeAppState, getUser, modifyAGgridRows, getSafePtr } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

// const { Option } = Select;
const { rz, $ } = window;

/*
LicenseManager.setLicenseKey(
  "[TRIAL]_16_May_2020_[v2]_MTU4OTU4NzIwMDAwMA==b03f1f5b63303eabbc3b42a734fcc666"
);
*/

ModuleRegistry.registerModules([
  AllEnterpriseModule,
  IntegratedChartsModule.with(AgChartsEnterpriseModule)
]);
provideGlobalGridOptions({ theme: "legacy"});

const renderState = {lastLNG: {}};

type Taggrid = {
  rowModelType?:"clientSide" | "serverSide" | "viewport" | "infinite",
  rowSelection:{
    mode:"singleRow" | "multiRow", // Раньше было: rowSelection?:"single" | "multiple"
    enableMultiSelectWithClick?:boolean, // Раньше было: rowMultiSelectWithClick
    enableClickSelection?:boolean | "enableDeselection" | "enableSelection", // Раньше было: suppressRowDeselection и suppressRowClickSelection
    copySelectedRows?:boolean, // Раньше было: suppressCopySingleCellRanges
    groupSelects?:"self" | "descendants" | "filteredDescendants", // Раньше было: groupSelectsChildren | groupSelectsFiltered
    checkboxes?:boolean,
    hideDisabledCheckboxes?:boolean,
    headerCheckbox?:boolean
  },
  cellSelection:boolean | { // Раньше было: enableRangeSelection
    suppressMultiRanges?:boolean // Раньше было: suppressMultiRangeSelection
  },
  rowGroupPanelShow?:"always" | "never" | "onlyWhenGrouping",
  treeData?:boolean, // Группировка в одном столбце, где путь указан КАК МАССИВ (например схема подчинения в организации). Обязательная функция в аттрибутах - getDataPath='userFunction'. Либо ее можно заменить ссылкой на имя поля так: getDataPath='path'
  rowHeight?:number, // Фиксированная высота строк AGgrid
  masterDetail?:boolean, // Вложенные таблицы
  detailRowHeight?:number, // Фиксированная высота вложенной AGgrid
  detailRowAutoHeight?:boolean, // Авто-высота вложенной AGgrid
  animateRows?:boolean, // Анимация строк
  groupDefaultExpanded?:number, // Группа раскрытая по-умолчанию
  tooltipShowDelay?:number,
  rowBuffer?:number,
  cacheQuickFilter?:boolean, quickFilterParser?:any
  enableCharts?:boolean, // Разрешить быстрые графики и диаграммы
  editType?:"fullRow" | undefined,
  readOnlyEdit:boolean,
  rowDragManaged?:boolean,
  stopEditingWhenCellsLoseFocus?:boolean,
  suppressMoveWhenRowDragging?:boolean,
  rowDragEntireRow?:boolean,
  rowDragMultiRow?:boolean, // Do not forget set rowSelection='multiple'
  suppressRowDrag?:boolean,
  maintainColumnOrder?:boolean,
  groupDisplayType?:"singleColumn" | "multipleColumns" | "groupRows" | "custom",
  enableAdvancedFilter?:boolean,
  pivotMode?:boolean,
  suppressAggFuncInHeader?:boolean
};

type sideBarType = {
  defaultToolPanel?:"filters" | "columns",
  position:"left" | "right", hiddenByDefault?:boolean,
  toolPanels: Array<"filters" | "columns" | {
      id:"filters" | "columns", iconKey: "columns" | "filter" | "menu",
      labelDefault:string, labelKey: "filters" | "columns",
      toolPanel:"agFiltersToolPanel" | "agColumnsToolPanel",
      toolPanelParams?:{
        suppressRowGroups?:boolean,
        suppressValues?:boolean,
        suppressPivots?:boolean,
        suppressPivotMode?:boolean,
        suppressColumnFilter?:boolean,
        suppressColumnSelectAll?:boolean,
        suppressColumnExpandAll?:boolean
      },
      minWidth:number, maxWidth:number, width:number
    }
  >
};

type statusBarSeriesType = "agTotalRowCountComponent" | "agTotalAndFilteredRowCountComponent" | "agFilteredRowCountComponent" | "agSelectedRowCountComponent" | "agAggregationComponent";

type statusBarType = {
  statusPanels:Array<{
    statusPanel:statusBarSeriesType,
    align?:"left" | "center" | "right"
  }>
};

// Defaut settings of Sidebar-panel
const sidebarPrototype = (needPanel:Array<"agFiltersToolPanel" | "agColumnsToolPanel" | "filters" | "columns">, columnsTitle?:string, filtersTitle?:string) => {
  const defSidebar:sideBarType = {
    position: "right",
    hiddenByDefault: false,
    toolPanels: []
  };

  if (rz.arrCompare(needPanel, ["agColumnsToolPanel", "columns"], "includesAny")) {
    defSidebar.toolPanels.push({
      id: "columns", iconKey: "columns",
      labelDefault: columnsTitle || "Columns",
      labelKey: "columns",
      toolPanel: "agColumnsToolPanel",
      toolPanelParams: {
        suppressRowGroups: true,
        suppressValues: true,
        suppressPivots: true,
        suppressPivotMode: true,
        suppressColumnFilter: true,
        suppressColumnSelectAll: false,
        suppressColumnExpandAll: false
      },
      minWidth: 225, maxWidth: 225, width: 225
    });
  }

  if (rz.arrCompare(needPanel, ["agFiltersToolPanel", "filters"], "includesAny")) {
    defSidebar.toolPanels.push({
      id: "filters", iconKey: "filter",
      labelDefault: filtersTitle || "Filters",
      labelKey: "filters",
      toolPanel: "agFiltersToolPanel",
      minWidth: 180, maxWidth: 400, width: 250
    });
  }

  return defSidebar;
};

// Defaut settings of Statusbar-panel
const statusbarPrototype = (needPanel:Array<statusBarSeriesType>) => {
  if (!needPanel || !needPanel.length) return undefined;
  const defStatusbar:statusBarType = {
    statusPanels: []
  };
  needPanel.forEach((pp) => { defStatusbar.statusPanels.push({statusPanel: pp}) });
  return defStatusbar;
};

// Sub-functions:
const quickFilterParser = (quickFilter) => ((quickFilter || "").replace((/,/igm), ";").split(";"));

// If User forgot set _id field - creates it automatically
const createRowsID = (rws) => {
  if (!rws || !rz.isArray(rws) || !rws.length) { return [] }
  const mrows = rz.objClone(rws);
  mrows.forEach((rr, r) => {
    if (rr && !rr._id) {
      mrows[r] = rz.objClone(rr);
      mrows[r]._id = (rr.id || rr.key || rz.strMakeID(24, true))
    }
  });
  return mrows;
};

// Main component:
const AGgrid:FC<TuniversalElementProps> = ({attributes, children}) => {
  AGgrid.displayName = "AGgrid";
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const theme = useAppSelector(getTheme);
  const compact = useAppSelector(getCompact);
  const isMobile = useAppSelector(getDesktopType);
  const dct = useAppSelector(getDict);
  const lng = useAppSelector(getLng);
  const safe = useAppSelector(getSafePtr);
  // const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath, type} = attributes;
  const appState = useAppSelector(getAppState(rootAttributes.key));
  const elState = appState[fullPath];
  const parentPath = rz.strParentFolderName(fullPath || ""), parentState = appState[parentPath];
  const rootNamespace = useMemo(() => (window[rootAttributes.key || rz.strMakeID(24)]), [rootAttributes.key]);

  // All attributes will be used through useMemo | useState | useCallback
  // to increase the performance

  // Available attributes (from attributes. or from elState.attributes.):
  // rowModelType, columnDefs, onGridReady, getContextMenuItems, rowData
  // defaultColDef, autoGroupColumnDef, rowGroupPanelShow, tooltipShowDelay, statusBar, sideBar, rowSelection, treeData,
  // groupDefaultExpanded, groupSelectsChildren, suppressRowClickSelection

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const gridRef = useRef<AgGridReact<any>>(null);

  const [rowSelection, setRowSelection] = useState<any>({
    mode: "singleRow", enableMultiSelectWithClick: false, enableClickSelection: true, groupSelects: "filteredDescendants",
    checkboxes: false, hideDisabledCheckboxes: false, headerCheckbox: false
  });
  const [defaultColDef, setDefaultColDef] = useState<any>({
    minWidth: 80, sortable: true, resizable: true, autoHeight: true, wrapText: true, enableRowGroup: false, enablePivot: false,
    menuTabs: ["filterMenuTab"], filter: "agSetColumnFilter", filterParams: { closeOnApply: true, buttons: ["cancel", "reset", "apply"], defaultToNothingSelected: true },
    cellClass: ["excelMultyLineText", "rz-text-line-height-normal"]
  });
  const [autoGroupColumnDef, setAutoGroupColumnDef] = useState<any>({minWidth: 350});
  const [columnDefs, setColumnDefs] = useState<Array<any>>([{field: "_id", headerName: "_id"}]);
  const [rowClassRules, setRowClassRules] = useState<any>(undefined);
  const [statusBar, setStatusBar] = useState<statusBarType | undefined>(undefined);
  const [sideBar, setSideBar] = useState<sideBarType | undefined>(undefined);
  const [detailCellRendererParams, setDetailCellRendererParams] = useState<any>(undefined);
  const [excelStyles, setExcelStyles] = useState<any>(undefined);
  const [defaultExcelExportParams, setDefaultExcelExportParams] = useState<any>(undefined);
  const [chartToolPanelsDef, setChartToolPanelsDef] = useState<any>(undefined);
  const [chartThemeOverrides, setChartThemeOverrides] = useState<any>(undefined);
  // const [rowData, setRowData] = useState<Array<any>>([]);

  const customComponents = useMemo(() => ({
    agMultiSelectCellEditor: SelectForAGgrid,
    agCascaderCellEditor: CascaderForAGgrid,
    agAutoCompleteCellEditor: AutocompleteForAGgrid
  }), []);

  // ====================================================
  // AGGRID-TYPE (clientSide | serverSide e.t.c.)
  // ====================================================
  const agType = useMemo<Taggrid>(() => {
    const typ:Taggrid = {
      rowModelType: "clientSide",
      rowSelection: {
        mode: "singleRow", enableMultiSelectWithClick: false, enableClickSelection: false, groupSelects: "filteredDescendants",
        checkboxes: false, hideDisabledCheckboxes: false, headerCheckbox: false
      },
      cellSelection: {suppressMultiRanges: true},
      tooltipShowDelay: 500, readOnlyEdit: true, animateRows: true
    };
    const attrStateKeys = rz.objKeys(elState.attributes);

    (type || "").replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt) => {
      if (~["clientSide", "clientside", "client"].indexOf(tt)) { typ.rowModelType = "clientSide" }
      else if (~["serverSide", "serverside", "server"].indexOf(tt)) { typ.rowModelType = "serverSide" }
      else if (~["singleSelection", "single"].indexOf(tt)) { typ.rowSelection.mode = "singleRow" }
      else if (~["multipleSelection", "multiple"].indexOf(tt)) { typ.rowSelection.mode = "multiRow" }
      else if (~["suppressRowDeselection", "suppressRowClickSelection"].indexOf(tt)) { typ.rowSelection.enableClickSelection = true }
      else if (~["checkboxes", "checkboxSelection"].indexOf(tt)) { typ.rowSelection.checkboxes = true; typ.rowSelection.headerCheckbox = true }
      else if (tt === "hideDisabledCheckboxes") { typ.rowSelection.hideDisabledCheckboxes = true }
      else if (tt === "rowMultiSelectWithClick") { typ.rowSelection.enableMultiSelectWithClick = true }
      else if (tt === "rowGroupPanelShow") { typ.rowGroupPanelShow = "always" }
      else if (tt === "groupSelectsChildren") { typ.rowSelection.groupSelects = "descendants" }
      else if (tt === "groupSelectsFiltered") { typ.rowSelection.groupSelects = "filteredDescendants" }
      else if (tt === "cacheQuickFilter") { typ.cacheQuickFilter = true; typ.quickFilterParser = quickFilterParser }
      else if (tt === "treeData") { typ.treeData = true }
      else if (tt === "masterDetail") { typ.masterDetail = true }
      else if (~["enableRangeSelection", "cellSelection"].indexOf(tt)) { typ.cellSelection = {suppressMultiRanges: true} }
      else if (~["multyRangeSelection"].indexOf(tt)) { typ.cellSelection = {suppressMultiRanges: false} }
      else if (tt === "enableCharts") { typ.enableCharts = true }
      else if (tt === "editFullRow") { typ.editType = "fullRow" }
      else if (tt === "rowDragManaged" || tt === "rowdragmanaged") { typ.rowDragManaged = true }
      else if (tt === "stopEditingWhenCellsLoseFocus") { typ.stopEditingWhenCellsLoseFocus = true }
      else if (tt === "suppressMoveWhenRowDragging" || tt === "suppressmovewhenrowdragging") { typ.suppressMoveWhenRowDragging = true }
      else if (tt === "rowDragMultiRow" || tt === "rowdragmultirow") { typ.rowDragMultiRow = true }
      else if (tt === "rowDragEntireRow" || tt === "rowdragentirerow") { typ.rowDragEntireRow = true }
      else if (tt === "suppressRowDrag" || tt === "suppressrowdrag") { typ.suppressRowDrag = true }
      else if (tt === "maintainColumnOrder" || tt === "maintaincolumnorder") { typ.maintainColumnOrder = true }
      else if (~["groupDisplayTypesingleColumn", "groupDisplayTypemultipleColumns", "groupDisplayTypegroupRows", "groupDisplayTypecustom"].indexOf(tt)) { typ.groupDisplayType = tt.replace("groupDisplayType", "") }
      else if (tt === "animateRows") { typ.animateRows = true }
      else if (tt === "noAnimateRows") { typ.animateRows = false }
      else if (tt === "enableAdvancedFilter" || tt === "enableadvancedfilter" || tt === "advancedFilter" || tt === "advancedfilter") { typ.enableAdvancedFilter = true }
      else if (tt === "pivotMode" || tt === "pivotmode") { typ.pivotMode = true }
      else if (tt === "suppressAggFuncInHeader" || tt === "suppressaggfuncinheader") { typ.suppressAggFuncInHeader = true }
    });

    if (attributes?.rowModelType) { typ.rowModelType = attributes.rowModelType }
    // Selection:
    if (attributes?.rowSelectionMode) { typ.rowSelection.mode = (attributes.rowSelectionMode === "singleRow" ? "singleRow" : "multiRow") }
    if (attributes?.enableMultiSelectWithClick) { typ.rowSelection.enableMultiSelectWithClick = (attributes.enableMultiSelectWithClick === "true") }
    if (attributes?.checkboxSelection) { typ.rowSelection.checkboxes = (attributes.checkboxSelection === "true") }
    if (attributes?.headerCheckbox) { typ.rowSelection.headerCheckbox = (attributes.headerCheckbox === "true") }
    if (attributes?.hideDisabledCheckboxes) { typ.rowSelection.hideDisabledCheckboxes = (attributes.hideDisabledCheckboxes === "true") }
    if (attributes?.enableClickSelection) {
      typ.rowSelection.enableClickSelection = rz.switchCase([
        attributes.enableClickSelection === "true", true,
        attributes.enableClickSelection === "false", false,
        attributes.enableClickSelection // "enableDeselection" | "enableSelection"
      ]);
    }
    if (attributes?.groupSelectsChildren === "true") { typ.rowSelection.groupSelects = "descendants" }
    if (attributes?.groupSelectsFiltered === "true") { typ.rowSelection.groupSelects = "filteredDescendants" }
    // Selection multy ranges
    if (attributes?.suppressMultiRangesSelection) { typ.cellSelection = {suppressMultiRanges: (attributes.suppressMultiRangesSelection === "true")} }
    if (attributes?.allowMultiRangesSelection) { typ.cellSelection = {suppressMultiRanges: (attributes.allowMultiRangesSelection === "false")} }

    // Grouping
    if (attributes?.groupDisplayType) { typ.groupDisplayType = attributes.groupDisplayType }
    if (attributes?.animateRows) { typ.animateRows = (attributes.animateRows === "true") }

    if (~attrStateKeys.indexOf("rowGroupPanelShow")) { typ.rowGroupPanelShow = elState.attributes.rowGroupPanelShow }
    else if (attributes?.rowGroupPanelShow) { typ.rowGroupPanelShow = attributes.rowGroupPanelShow }

    if (~attrStateKeys.indexOf("treeData")) { typ.treeData = elState.attributes.treeData }
    else if (attributes?.treeData) { typ.treeData = (attributes.treeData === "true") }

    if (attributes?.cacheQuickFilter) { typ.cacheQuickFilter = (attributes.cacheQuickFilter === "true"); typ.quickFilterParser = quickFilterParser }
    if (attributes?.tooltipShowDelay) { typ.tooltipShowDelay = Number(attributes.tooltipShowDelay) }
    if (attributes?.groupDefaultExpanded) { typ.groupDefaultExpanded = Number(attributes.groupDefaultExpanded) }
    if (attributes?.masterDetail) { typ.masterDetail = (attributes.masterDetail === "true") }
    if (attributes?.rowHeight) { typ.rowHeight = Number(attributes.rowHeight) }
    if (attributes?.detailRowHeight) { typ.detailRowHeight = Number(attributes.detailRowHeight) }
    if (attributes?.detailRowAutoHeight) { typ.detailRowAutoHeight = (attributes.detailRowAutoHeight === "true") }

    if (~attrStateKeys.indexOf("rowBuffer")) { typ.rowBuffer = Number(elState.attributes.rowBuffer) }
    else if (attributes?.rowBuffer) { typ.rowBuffer = Number(attributes.rowBuffer) }

    if (~attrStateKeys.indexOf("cellSelection")) { typ.cellSelection = (elState.attributes.cellSelection === "true" || elState.attributes.cellSelection === true) }
    else if (~attrStateKeys.indexOf("enableRangeSelection")) { typ.cellSelection = (elState.attributes.enableRangeSelection === "true" || elState.attributes.enableRangeSelection === true) }
    else if (attributes?.cellSelection) { typ.cellSelection = (attributes.cellSelection === "true") }
    else if (attributes?.enableRangeSelection) { typ.cellSelection = (attributes.enableRangeSelection === "true") }

    if (~attrStateKeys.indexOf("enableCharts")) { typ.enableCharts = (elState.attributes.enableCharts === "true" || elState.attributes.enableCharts === true) }
    else if (attributes?.enableCharts) { typ.enableCharts = (attributes.enableCharts === "true") }

    if (typ.enableCharts) {
      if (!attributes?.chartToolPanelsDef) {
        setChartToolPanelsDef({ defaultToolPanel: "data", dataPanel: { groups: [{ type: "seriesChartType", isOpen: false }, { type: "series", isOpen: false }] } });
      }
      if (!attributes?.chartThemeOverrides) {
        setChartThemeOverrides({ common: {title: { enabled: true, text: dct.ag.quickChart}}, bar: {axes: {category: {label: {rotation: 0}}}} });
      }
    }

    if (attributes?.readOnlyEdit) { typ.readOnlyEdit = (attributes.readOnlyEdit === "true") }
    if (attributes?.editType || attributes?.edittype) { typ.editType = ((attributes.editType || attributes.edittype) === "fullRow" ? attributes.editType : undefined) }

    if (attributes?.rowDragManaged) { typ.rowDragManaged = (attributes.rowDragManaged === "true") }
    if (attributes?.stopEditingWhenCellsLoseFocus) { typ.stopEditingWhenCellsLoseFocus = (attributes.stopEditingWhenCellsLoseFocus === "true") }
    if (attributes?.suppressMoveWhenRowDragging) { typ.suppressMoveWhenRowDragging = (attributes.suppressMoveWhenRowDragging === "true") }
    if (attributes?.rowDragMultiRow) { typ.rowDragMultiRow = (attributes.rowDragMultiRow === "true") }
    if (attributes?.rowDragEntireRow) { typ.rowDragEntireRow = (attributes.rowDragEntireRow === "true") }

    if (~attrStateKeys.indexOf("suppressRowDrag")) { typ.suppressRowDrag = (elState.attributes.suppressRowDrag === "true" || elState.attributes.suppressRowDrag === true) }
    else if (attributes?.suppressRowDrag) { typ.suppressRowDrag = (attributes.suppressRowDrag === "true") }
    else if (attributes?.maintainColumnOrder) { typ.maintainColumnOrder = (attributes.maintainColumnOrder === "true") }

    if (attributes?.enableAdvancedFilter) { typ.enableAdvancedFilter = (attributes.enableAdvancedFilter === "true") }
    else if (attributes?.advancedFilter) { typ.enableAdvancedFilter = (attributes.advancedFilter === "true") }

    if (elState.attributes?.pivotMode) { typ.pivotMode = Boolean(elState.attributes.pivotMode === true || elState.attributes.pivotMode === "true") }
    else if (attributes?.pivotMode) { typ.pivotMode = (attributes.pivotMode === "true") }
    else if (attributes?.pivotmode) { typ.pivotMode = (attributes.pivotmode === "true") }

    if (attributes?.suppressAggFuncInHeader) { typ.suppressAggFuncInHeader = (attributes.suppressAggFuncInHeader === "true") }

    return typ;
  }, [type, attributes, dct, elState.attributes]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    if (!rz.arrContains(userClss, (/^ag-theme-/igm))) { userClss.push("ag-theme-" + (compact ? "balham" : "alpine") + (theme === "dark" ? "-dark" : "")) }
    return userClss;
  }, [compact, elState?.disabled, elState?.userClass, permissions.disabled, theme]);

  const [gridReady, setGridReady] = useState(false);
  const [gridFirstDataRenderred, setGridFirstDataRenderred] = useState(false);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    if (!est.height && gridReady) {
      const desktopEl = $("#virtual-desktop")[0], mainToolbar = $("#layout-main-toolbar")[0];
      // @ts-ignore
      est.height = $(desktopEl).innerHeight() - $(mainToolbar).outerHeight() - 2; // Default height = desktop.height - mainToolbar.height - 2
      if (~["container", "cell", "div", "span", "flex", "form", "modal", "space", "tab", "step", "item"].indexOf(parentState?.name)) {
        const parentElement = $("#" + parentState.key);
        if (parentElement[0]) {
          const lastEl = $(parentElement[0]).children().eq(-2);
          if (lastEl[0]) {
            // @ts-ignore
            est.height = window.innerHeight - ($(lastEl[0]).offset().top + $(lastEl[0]).outerHeight()) - 4;
          }
          else {
            const chTop = $(parentElement[0]).offset().top + parseInt($(parentElement[0]).css("padding-top"), 10) + parseInt($(parentElement[0]).css("padding-bottom"), 10);
            // @ts-ignore
            est.height = window.innerHeight - chTop - 2;
          }
        }
      }
    }
    if (!est.boxSizing) { est.boxSizing = "border-box" }
    return est;
  }, [parentState, elState.userStyle, elState?.visible, permissions.visible, gridReady]);

  const localeText = useMemo(() => {
    if (lng === "ru") { return AG_GRID_LOCALE_RU }
    return AG_GRID_LOCALE_EN;
  }, [lng]);

  // ====================================================
  // Configure rowSelection
  // ====================================================
  useEffect(() => {
    const attr = (attributes?.rowSelection || attributes?.rowselection || "");
    if (!attr) { setRowSelection(agType.rowSelection); return }
    if (attr.startsWith("{") && attr.endsWith("}")) {
      const jsn = rz.objFromJSON((rz.strReplaceAll(attr, "'", "\"")).trim());
      if (jsn) { setRowSelection(jsn) }
    }
    else if (rootNamespace && rootNamespace[attr]) {
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, safe}).then((ret) => {
          if (ret && rz.isObject(ret)) { setRowSelection(ret) }
        }).catch((err) => { console.log(err) });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, safe});
        if (ret && rz.isObject(ret)) { setRowSelection(ret) }
      }
      else if (rz.isObject(clientFNC)) {
        setRowSelection(clientFNC);
      }
    }
  }, [agType.rowSelection, attributes, dct, key, lng, rootAttributes.key, rootNamespace, safe, user]);

  // ====================================================
  // Configure rowClassRules
  // ====================================================
  useEffect(() => {
    const attr = (attributes?.rowClassRules || attributes?.rowclassrules || "");
    if (!attr) { return }
    if (attr.startsWith("{") && attr.endsWith("}")) {
      const jsn = rz.objFromJSON((rz.strReplaceAll(attr, "'", "\"")).trim());
      if (jsn) { setRowClassRules(jsn) }
    }
    else if (rootNamespace && rootNamespace[attr]) {
      const clientFNC = rootNamespace[attr];
      if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, safe});
        if (ret && rz.isObject(ret)) { setRowClassRules(ret) }
      }
      else if (rz.isObject(clientFNC)) {
        setRowClassRules(clientFNC);
      }
    }
  }, [key, lng, rootAttributes.key, attributes, rootNamespace, user, isMobile, dct, safe]);

  // ====================================================
  // Configure defaultColDef
  // ====================================================
  useEffect(() => {
    const attr = (attributes?.defaultColDef || attributes?.defaultcoldef || "");
    if (!attr) { return }
    if (attr.startsWith("{") && attr.endsWith("}")) {
      const jsn = rz.objFromJSON((rz.strReplaceAll(attr, "'", "\"")).trim());
      if (jsn) { setDefaultColDef(jsn) }
    }
    else if (rootNamespace && rootNamespace[attr]) {
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, safe}).then((ret) => {
          if (ret && rz.isObject(ret)) { setDefaultColDef(ret) }
        }).catch((err) => { console.log(err) });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, safe});
        if (ret && rz.isObject(ret)) { setDefaultColDef(ret) }
      }
      else if (rz.isObject(clientFNC)) {
        setDefaultColDef(clientFNC);
      }
    }
  }, [key, lng, rootAttributes.key, attributes, rootNamespace, user, isMobile, dct, safe]);

  // ====================================================
  // columnDefs - Configure columns
  // ====================================================
  useEffect(() => {
    // Custom Cell-Editor Components:
    // ++++++++++++++++++++++++++++++++
    const checkCustomEditComponents = (columnDef) => {
      if (!columnDef || !columnDef.length) { return [] }
      columnDef.forEach((col) => {
        if (col.cellEditor === "agMultiSelectCellEditor") { col.cellEditor = SelectForAGgrid }
        if (col.cellEditor === "agAutoCompleteCellEditor") { col.cellEditor = AutocompleteForAGgrid; col.suppressKeyboardEvent = (e) => (e.editing && e.event.key === "Enter") }
        if (col.cellEditor === "agCascaderCellEditor") { col.cellEditor = CascaderForAGgrid }
      });
      return columnDef;
    };
    // ++++++++++++++++++++++++++++++++

    const attr = (attributes?.columnDefs || attributes?.columndefs || "");
    if (attr.startsWith("{") && attr.endsWith("}")) {
      const jsn = rz.objFromJSON((rz.strReplaceAll(attr, "'", "\"")).trim());
      if (jsn) {
        const okeys = rz.objKeys(jsn);
        if (okeys[0] && rz.isArray(jsn[okeys[0]])) { setColumnDefs(checkCustomEditComponents(jsn[okeys[0]])) }
      }
    }
    else if (attr && rootNamespace && rootNamespace[attr]) {
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, safe}).then((ret) => {
          if (ret && rz.isArray(ret)) { setColumnDefs(checkCustomEditComponents(ret)) }
        }).catch((err) => { console.log(err) });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, safe});
        if (ret && rz.isArray(ret)) { setColumnDefs(checkCustomEditComponents(ret)) }
      }
      else if (rz.isArray(clientFNC)) {
        setColumnDefs(clientFNC);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lng, key, rootAttributes.key, attributes, rootNamespace?.columnDefs, user?.login, elState?.specialParams?.columnDefs, isMobile]);

  // ====================================================
  // Configure autoGroupColumnDef
  // ====================================================
  useEffect(() => {
    const attr = (attributes?.autoGroupColumnDef || attributes?.autogroupcolumndef || "");
    if (!attr) { return }
    if (attr.startsWith("{") && attr.endsWith("}")) {
      const jsn = rz.objFromJSON((rz.strReplaceAll(attr, "'", "\"")).trim());
      if (jsn) { setAutoGroupColumnDef(jsn) }
    }
    else if (rootNamespace && rootNamespace[attr]) {
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, safe}).then((ret) => {
          if (ret && rz.isObject(ret)) { setAutoGroupColumnDef(ret) }
        }).catch((err) => { console.log(err) });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, safe});
        if (ret && rz.isObject(ret)) { setAutoGroupColumnDef(ret) }
      }
      else if (rz.isObject(clientFNC)) {
        setAutoGroupColumnDef(clientFNC);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributes, key, lng, rootAttributes.key, rootNamespace, user, elState?.specialParams?.autoGroupColumnDef, isMobile, safe]);

  // ====================================================
  // Configure statusBar
  // ====================================================
  useEffect(() => {
    const attr = (attributes?.statusBar || attributes?.statusbar || "");
    const aa = attr.replace((/,|;|\s+|\|/igm), " ").trim().split(" ");
    if (!attr) { setStatusBar(undefined) }
    else if (attr.startsWith("{") && attr.endsWith("}")) {
      const jsn = rz.objFromJSON((rz.strReplaceAll(attr, "'", "\"")).trim());
      // @ts-ignore
      if (jsn) { setStatusBar(jsn) }
    }
    else if (rz.arrCompare(aa, ["agTotalRowCountComponent", "agTotalAndFilteredRowCountComponent", "agFilteredRowCountComponent", "agSelectedRowCountComponent", "agAggregationComponent"], "includesAny")) {
      setStatusBar(statusbarPrototype(aa));
    }
    else if (attr && rootNamespace && rootNamespace[attr]) {
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, safe}).then((ret) => {
          if (ret && rz.isObject(ret)) { setStatusBar(ret) }
        }).catch((err) => { console.log(err) });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, safe});
        if (ret && rz.isObject(ret)) { setStatusBar(ret) }
      }
      else if (rz.isObject(clientFNC)) {
        setStatusBar(clientFNC);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attributes, rootAttributes.key, lng, rootNamespace, user?.login, elState?.specialParams?.statusBar, isMobile, safe]);

  // ====================================================
  // Configure sideBar
  // ====================================================
  useEffect(() => {
    const attr = (attributes?.sideBar || attributes?.sidebar || "");
    const aa = attr.replace((/,|;|\s+|\|/igm), " ").trim().split(" ");
    if (!attr) { setSideBar(undefined) }
    else if ((attr || "").startsWith("{") && (attr || "").endsWith("}")) {
      const jsn = rz.objFromJSON((rz.strReplaceAll(attr, "'", "\"")).trim());
      // @ts-ignore
      if (jsn) { setSideBar(jsn) }
    }
    else if (rz.arrCompare(aa, ["columns", "filters", "agColumnsToolPanel", "agFiltersToolPanel"], "includesAny")) {
      setSideBar(sidebarPrototype(aa, dct.ag.agColumnsToolPanel, dct.ag.agFiltersToolPanel));
    }
    else if (rootNamespace && rootNamespace[attr]) {
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, safe}).then((ret) => {
          if (ret && rz.isObject(ret)) { setSideBar(ret) }
        }).catch((err) => { console.log(err) });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, safe});
        if (ret && rz.isObject(ret)) { setSideBar(ret) }
      }
      else if (rz.isObject(clientFNC)) {
        setSideBar(clientFNC);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributes, key, lng, rootNamespace, user?.login, elState?.specialParams?.sideBar, isMobile, safe]);

  // ====================================================
  // AGgrid dynamic rows height
  // ====================================================
  const getRowHeight = useMemo(() => {
    const attr = (attributes?.getRowHeight || attributes?.getrowheight || "");
    if (attr && rootNamespace && rootNamespace[attr]) {
      const clientFNC = rootNamespace[attr];
      if (rz.isFunction(clientFNC)) { return clientFNC }
    }
    return undefined;
  }, [attributes, rootNamespace]);

  // ====================================================
  // getChildCount
  // ====================================================
  const getChildCount = useCallback((data) => {
    const attr = (attributes?.getChildCount || attributes?.getchildcount || "");
    if (attr && rootNamespace && rootNamespace[attr]) {
      const clientFNC = rootNamespace[attr];
      if (rz.isFunction(clientFNC)) { return clientFNC(data) }
    }
    return undefined;
  }, [attributes, rootNamespace]);

  // ====================================================
  // Configure sub-AGgrid tables
  // ====================================================
  const getRowId = useMemo(() => {
    const attr = (attributes?.getRowId || attributes?.getrowid || "");
    if (!attr) { // getRowId was not specified by User - use default function from AGgrid manual
      if (agType.rowModelType === "serverSide") {
        // console.log("getRowId for serverSide rowModelType");
        return (params1) => {
          const parentKeysJoined = (params1.parentKeys || []).join("-");
          if (params1.data._id != null || params1.data.id != null) { return parentKeysJoined + (params1.data._id || params1.data.id) }
          const rowGroupCols = params1.api.getRowGroupColumns();
          const thisGroupCol = rowGroupCols[params1.level];
          return parentKeysJoined + ((thisGroupCol && thisGroupCol.getColDef && params1.data[thisGroupCol.getColDef().field]) || "-");
        };
      }
      return (params) => (params && params.data && (params.data._id || params.data.id));
    }
    if (~["rowkey", "key", "id", "_id"].indexOf(attr)) { return (params) => (params && params.data && params.data[attr]) }
    if (rootNamespace && rootNamespace[attr]) {
      const clientFNC = rootNamespace[attr];
      if (rz.isFunction(clientFNC)) { return clientFNC }
    } // Можно указать в атрибуте getRowId="ИмяСтолбца" (просто key столбца и не более)
    return undefined;
  }, [agType.rowModelType, attributes, rootNamespace]);
  // ====================================================
  // Second step of Configure sub-AGgrid tables
  // ====================================================
  useEffect(() => {
    const attr = (attributes?.detailCellRendererParams || attributes?.detailcellrendererparams || "");
    if (attr.startsWith("{") && attr.endsWith("}")) {
      const jsn = rz.objFromJSON((rz.strReplaceAll(attr, "'", "\"")).trim());
      if (jsn) { setDetailCellRendererParams(jsn) }
    }
    else if (attr && rootNamespace && rootNamespace[attr]) {
      const clientFNC = rootNamespace[attr];
      if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, safe});
        if (ret && rz.isObject(ret)) { setDetailCellRendererParams(ret) }
      }
      else if (rz.isObject(clientFNC)) {
        setDetailCellRendererParams(clientFNC);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributes, lng, key, rootAttributes.key, rootNamespace, user?.login, elState?.specialParams?.detailCellRendererParams, safe]);

  // ====================================================
  // excelStyles
  // ====================================================
  useEffect(() => {
    const attr = (attributes?.excelStyles || attributes?.excelstyles || "");
    if (attr.startsWith("{") && attr.endsWith("}")) {
      const jsn = rz.objFromJSON((rz.strReplaceAll(attr, "'", "\"")).trim());
      if (jsn) {
        const okeys = rz.objKeys(jsn);
        if (okeys[0] && rz.isArray(jsn[okeys[0]])) { setExcelStyles(jsn[okeys[0]]) }
      }
    }
    else if (attr && rootNamespace && rootNamespace[attr]) {
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, safe}).then((ret) => {
          if (ret && rz.isArray(ret)) { setExcelStyles(ret) }
        }).catch((err) => { console.log(err) });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, safe});
        if (ret && rz.isArray(ret)) { setExcelStyles(ret) }
      }
      else if (rz.isArray(clientFNC)) {
        setExcelStyles(clientFNC);
      }
    }
  }, [attributes, key, rootNamespace, rootAttributes.key, user, dct, lng, safe]);

  // ====================================================
  // defaultExcelExportParams
  // ====================================================
  useEffect(() => {
    const attr = (attributes?.defaultExcelExportParams || attributes?.defaultexcelexportparams || "");
    if (attr.startsWith("{") && attr.endsWith("}")) {
      const jsn = rz.objFromJSON((rz.strReplaceAll(attr, "'", "\"")).trim());
      if (jsn) { setDefaultExcelExportParams(jsn) }
    }
    else if (attr && rootNamespace && rootNamespace[attr]) {
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, safe}).then((ret) => {
          if (ret && rz.isObject(ret)) { setDefaultExcelExportParams(ret) }
        }).catch((err) => { console.log(err) });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, safe});
        if (ret && rz.isObject(ret)) { setDefaultExcelExportParams(ret) }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lng, attributes, key, rootNamespace, user?.login, rootAttributes.key, safe]);

  // ====================================================
  // chartToolPanelsDef
  // ====================================================
  useEffect(() => {
    const attr = (attributes?.chartToolPanelsDef || attributes?.charttoolpanelsdef || "");
    if (attr.startsWith("{") && attr.endsWith("}")) {
      const jsn = rz.objFromJSON((rz.strReplaceAll(attr, "'", "\"")).trim());
      if (jsn) { setChartToolPanelsDef(jsn) }
    }
    else if (attr && rootNamespace && rootNamespace[attr]) {
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, safe}).then((ret) => {
          if (ret && rz.isObject(ret)) { setChartToolPanelsDef(ret) }
        }).catch((err) => { console.log(err) });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, safe});
        if (ret && rz.isObject(ret)) { setChartToolPanelsDef(ret) }
      }
      else if (rz.isObject(clientFNC)) {
        setChartToolPanelsDef(clientFNC);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lng, attributes, key, rootNamespace, user?.login, rootAttributes.key, safe]);

  // ====================================================
  // chartThemeOverrides
  // ====================================================
  useEffect(() => {
    const attr = (attributes?.chartThemeOverrides || attributes?.chartthemeoverrides || "");
    if (attr.startsWith("{") && attr.endsWith("}")) {
      const jsn = rz.objFromJSON((rz.strReplaceAll(attr, "'", "\"")).trim());
      if (jsn) { setChartThemeOverrides(jsn) }
    }
    else if (attr && rootNamespace && rootNamespace[attr]) {
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, safe}).then((ret) => {
          if (ret && rz.isObject(ret)) { setChartThemeOverrides(ret) }
        }).catch((err) => { console.log(err) });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, safe});
        if (ret && rz.isObject(ret)) { setChartThemeOverrides(ret) }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lng, attributes, key, rootNamespace, user?.login, rootAttributes.key, safe]);

  // ====================================================
  // Right click (Context menu)
  // ====================================================
  const getContextMenuItems = useCallback((params) => {
    const attr = (attributes?.getContextMenuItems || attributes?.getcontextmenuitems || "");
    const nodeClicked = params?.node;
    if (!gridRef?.current || !nodeClicked || !params?.column) { return [] }
    const selectedColumn = params.column?.colId, selectedCell = nodeClicked?.data && selectedColumn && nodeClicked.data[selectedColumn];
    const colState = gridRef?.current?.api.getColumnState() || [], sortActive = colState.some((c) => c.sort), filterActive = gridRef?.current?.api.isAnyFilterPresent();
    const setFilters = gridRef?.current?.api.getFilterModel(), filterKeys = rz.objKeys(setFilters || {}), groupColumns = gridRef?.current?.api.getRowGroupColumns();
    const cellRanges = gridRef?.current?.api.getCellRanges();
    // let checkedNodes:IRowNode<any>[] = [];
    // if (agType.rowModelType === "serverSide") { const selSt = gridRef?.current?.api.getServerSideSelectionState(); console.log(selSt) } // НЕ ДОРАБОТАНО!!!
    // else { checkedNodes = gridRef?.current?.api.getSelectedNodes() }
    const checkedNodes = gridRef?.current?.api.getSelectedNodes();

    const selectedNodes:Array<any> = [];
    if (cellRanges && cellRanges.length === 1 && cellRanges[0].startRow && cellRanges[0].endRow) {
      const startRow = Math.min(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex), endRow = Math.max(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex);
      if (agType.rowModelType === "serverSide") {
        gridRef?.current?.api.forEachNode((node) => {
          // @ts-ignore
          if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow) { selectedNodes.push(node) }
        });
      }
      else {
        gridRef?.current?.api.forEachNodeAfterFilterAndSort((node) => {
          // @ts-ignore
          if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow) { selectedNodes.push(node) }
        });
      }
    }

    // Этот вариант будет лучше, т.к. предусматривает несколько диапазонов выделенных ячеек:
    // (cellRanges || []).forEach((cr) => {
    //   if (cr.startRow && cr.endRow) {
    //     const startRow = Math.min(cr.startRow.rowIndex, cr.endRow.rowIndex), endRow = Math.max(cr.startRow.rowIndex, cr.endRow.rowIndex);
    //     if (agType.rowModelType === "serverSide") {
    //       gridRef?.current?.api.forEachNode((node) => {
    //         // @ts-ignore
    //         if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow && !selectedNodes.some((nd) => (rz.objToJSON(nd.data) === rz.objToJSON(node.data)))) { selectedNodes.push(node) }
    //       });
    //     }
    //     else {
    //       gridRef?.current?.api.forEachNodeAfterFilterAndSort((node) => {
    //         // @ts-ignore
    //         if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow && !selectedNodes.some((nd) => (rz.objToJSON(nd.data) === rz.objToJSON(node.data)))) { selectedNodes.push(node) }
    //       });
    //     }
    //   }
    // });

    if (fnc.config?.isDev) {
      // eslint-disable-next-line max-len
      console.log({action: "<AGgrid key='" + key + "'> -> getContextMenuItems()", nodeClicked, selectedNodes, checkedNodes, selectedColumn, selectedCell, cellRanges, colState, sortActive, setFilters, filterActive, filterKeys, groupColumns });
    }
    const allComands = (attr.replace((/,/gm), ";")).split(";");
    // eslint-disable-next-line max-len
    if (rz.arrCompare(allComands, ["copy", "cut", "paste", "separator", "resetColumns", "autoSizeAll", "expandAll", "contractAll", "copyWithHeaders", "copyWithGroupHeaders", "export", "excelExport", "csvExport", "chartRange", "pivotChart"], "includesAny")) {
      return allComands;
    }
    if (rootNamespace && attr && rootNamespace[attr]) {
      const clientFNC = rootNamespace[attr];
      if (rz.isFunction(clientFNC)) {
        // eslint-disable-next-line max-len
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, nodeClicked, selectedNodes, checkedNodes, selectedColumn, selectedCell, cellRanges, colState, sortActive, setFilters, filterActive, filterKeys, groupColumns, gridRef: gridRef.current, lng, dct, user, safe});
        if (ret && rz.isArray(ret)) { return ret }
      } else { return [] }
    }
    else { return [] }
  }, [attributes, rootNamespace, agType.rowModelType, key, rootAttributes.key, lng, dct, user, safe]);

  // ====================================================
  // getDataPath (treeData is tuned on) - tree view
  // ====================================================
  const getDataPath = useCallback((oneNodeData) => {
    const attr = (attributes?.getDataPath || attributes?.getdatapath || "");
    if (!attr) { return undefined }
    // Simple way - attr 'getDataPath' contains field name with prepared 'path'
    if (oneNodeData && oneNodeData[attr] && rz.isArray(oneNodeData[attr])) { return oneNodeData[attr] }
    // Client function:
    if (rootNamespace && attr && rootNamespace[attr]) {
      const clientFNC = rootNamespace[attr];
      if (rz.isFunction(clientFNC)) {
        const allNodes:Array<any> = [], allRows:Array<any> = [];
        gridRef?.current?.api.forEachNode((nn) => { allNodes.push(nn); if (nn && nn.data) { allRows.push(nn.data) } });

        const ret = clientFNC({rowData: oneNodeData, allNodes, allRows, appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef.current, lng, dct, safe});
        if (ret && rz.isArray(ret)) { return ret }
      }
      return [];
    }
    return undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lng, attributes, key, rootNamespace, user?.login, rootAttributes.key, safe]);

  // ====================================================
  // onCellValueChanged
  // ====================================================
  const onCellValueChanged = useCallback((params) => {
    const attr = (attributes?.onCellValueChanged || attributes?.oncellvaluechanged || "");
    if (attr && rootNamespace && rootNamespace[attr]) {
      if (fnc.config?.isDev) console.log({action: "<AGgrid key='" + key + "'> -> onCellValueChanged()"});
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, params, safe}).then((ret) => {
          if (ret) {
            if (ret?.err) { console.log(ret.err) }
            if (ret?.state?.fullPath && ret?.state?.newState) {
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
            }
          }
        }).catch((err) => { console.log(err) });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, params, safe});
        if (ret) {
          if (ret?.err) { console.log(ret.err) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attributes, rootNamespace, rootAttributes.key, user?.login, lng, dispatch, safe]);

  // ====================================================
  // onCellEditingStarted
  // ====================================================
  const onCellEditingStarted = useCallback((params) => {
    const attr = (attributes?.onCellEditingStarted || attributes?.oncelleditingstarted || "");
    if (attr && rootNamespace && rootNamespace[attr]) {
      if (fnc.config?.isDev) console.log({action: "<AGgrid key='" + key + "'> -> onCellEditingStarted()"});
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, params, safe}).then((ret) => {
          if (ret) {
            if (ret?.err) { console.log(ret.err) }
            if (ret?.state?.fullPath && ret?.state?.newState) {
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
            }
          }
        }).catch((err) => { console.log(err) });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, params, safe});
        if (ret) {
          if (ret?.err) { console.log(ret.err) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attributes, rootNamespace, rootAttributes.key, user?.login, lng, dispatch, safe]);

  // ====================================================
  // onCellEditingStopped
  // ====================================================
  const onCellEditingStopped = useCallback((params) => {
    const attr = (attributes?.onCellEditingStopped || attributes?.onCelleditingstopped || "");
    if (attr && rootNamespace && rootNamespace[attr]) {
      if (fnc.config?.isDev) console.log({action: "<AGgrid key='" + key + "'> -> onCellEditingStopped()"});
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, params, safe}).then((ret) => {
          if (ret) {
            if (ret?.err) { console.log(ret.err) }
            if (ret?.state?.fullPath && ret?.state?.newState) {
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
            }
          }
        }).catch((err) => { console.log(err) });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, params, safe});
        if (ret) {
          if (ret?.err) { console.log(ret.err) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attributes, rootNamespace, rootAttributes.key, user?.login, lng, dispatch, safe]);

  // ====================================================
  // onCellEditRequest
  // ====================================================
  const onCellEditRequest = useCallback((params) => {
    const attr = (attributes?.onCellEditRequest || attributes?.onCelleditrequest || "");

    // User himself should apply changes to Redux-state: window.modifyGridRows({ appkey, fullPath: attributes.fullPath, editRows: [{...params.data, [params.column.colId]: params.newValue}] });
    if (attr && rootNamespace && rootNamespace[attr]) {
      if (fnc.config?.isDev) console.log({action: "<AGgrid key='" + key + "'> -> onCellEditRequest()"});
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, params, safe}).then((ret) => {
          if (ret) {
            if (ret?.err) { console.log(ret.err) }
            if (ret?.state?.fullPath && ret?.state?.newState) {
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
            }
          }
        }).catch((err) => { console.log(err) });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, params, safe});
        if (ret) {
          if (ret?.err) { console.log(ret.err) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        }
      }
    } // If there is no a clientFNC to modify Redux-state, apply changes force to state here:
    else if (gridRef.current?.api && params?.column?.colId && !rz.isUndefinedOrNull(params?.newValue) && params?.newValue === params?.oldValue) {
      // Apply changes to Redux-state:
      dispatch(modifyAGgridRows({editRows: [{...params.data, [params.column.colId]: params.newValue}], appkey: rootAttributes.key, gridPath: attributes.fullPath}));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attributes, rootNamespace, rootAttributes.key, user?.login, lng, dispatch, safe]);

  // ====================================================
  // onRowEditingStopped
  // ====================================================
  const onRowEditingStopped = useCallback((params) => {
    const attr = (attributes?.onRowEditingStopped || attributes?.onroweditingstopped || "");
    if (attr && rootNamespace && rootNamespace[attr]) {
      if (fnc.config?.isDev) console.log({action: "<AGgrid key='" + key + "'> -> onRowEditingStopped()"});
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, params, safe}).then((ret) => {
          if (ret) {
            if (ret?.err) { console.log(ret.err) }
            if (ret?.state?.fullPath && ret?.state?.newState) {
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
            }
          }
        }).catch((err) => { console.log(err) });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, params, safe});
        if (ret) {
          if (ret?.err) { console.log(ret.err) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attributes, rootNamespace, rootAttributes.key, user?.login, lng, dispatch, safe]);

  // ====================================================
  // onRowDragEnter
  // ====================================================
  const onRowDragEnter = useCallback((event) => {
    const attr = (attributes?.onRowDragEnter || attributes?.onrowdragenter || "");
    if (attr && rootNamespace && rootNamespace[attr]) {
      const movingNode = event.node, movingNodes = event.nodes, movingData = movingNode.data, {vDirection} = event;
      const colState = gridRef?.current?.api.getColumnState() || [], sortActive = colState.some((c) => c.sort), filterActive = gridRef?.current?.api.isAnyFilterPresent();
      const setFilters = gridRef?.current?.api.getFilterModel(), filterKeys = rz.objKeys(setFilters || {}), groupColumns = gridRef?.current?.api.getRowGroupColumns();
      const cellRanges = gridRef?.current?.api.getCellRanges(), checkedNodes = gridRef?.current?.api.getSelectedNodes(), selectedNodes:Array<any> = [];
      if (cellRanges && cellRanges.length === 1 && cellRanges[0].startRow && cellRanges[0].endRow) {
        const startRow = Math.min(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex), endRow = Math.max(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex);
        if (agType.rowModelType === "serverSide") {
          gridRef?.current?.api.forEachNode((node) => {
            // @ts-ignore
            if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow) { selectedNodes.push(node) }
          });
        }
        else {
          gridRef?.current?.api.forEachNodeAfterFilterAndSort((node) => {
            // @ts-ignore
            if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow) { selectedNodes.push(node) }
          });
        }
      }
      // eslint-disable-next-line max-len
      if (fnc.config?.isDev) console.log({action: "<AGgrid key='" + key + "'> -> onRowDragEnter()", movingNode, movingNodes, movingData, vDirection, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns});
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        // eslint-disable-next-line max-len
        clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, movingNode, movingNodes, movingData, vDirection, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns, safe}).then((ret) => {
          if (ret) {
            if (ret?.err) { console.log(ret.err) }
            if (ret?.cancel) { gridRef?.current?.api.clearFocusedCell() }
            if (rz.isBoolean(ret?.suppressRowDrag)) { gridRef?.current?.api.setGridOption("suppressRowDrag", ret.suppressRowDrag) }
            else if (rz.isBoolean(agType.suppressRowDrag)) { gridRef?.current?.api.setGridOption("suppressRowDrag", Boolean(agType.suppressRowDrag)) }
            if (ret?.state?.fullPath && ret?.state?.newState) {
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
            }
          }
          gridRef?.current?.api.clearFocusedCell();
        }).catch((err) => { console.log(err) });
      }
      else if (rz.isFunction(clientFNC)) {
        // eslint-disable-next-line max-len
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, movingNode, movingNodes, movingData, vDirection, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns, safe});
        if (ret) {
          if (ret?.err) { console.log(ret.err) }
          if (ret?.cancel) { gridRef?.current?.api.clearFocusedCell() }
          if (rz.isBoolean(ret?.suppressRowDrag)) { gridRef?.current?.api.setGridOption("suppressRowDrag", ret.suppressRowDrag) }
          else if (rz.isBoolean(agType.suppressRowDrag)) { gridRef?.current?.api.setGridOption("suppressRowDrag", Boolean(agType.suppressRowDrag)) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        }
        gridRef?.current?.api.clearFocusedCell();
      }
    }
  }, [attributes, rootNamespace, key, agType.rowModelType, agType.suppressRowDrag, rootAttributes.key, user, lng, dct, dispatch, safe]);

  // ====================================================
  // onRowDragMove
  // ====================================================
  const onRowDragMove = useCallback((event) => {
    const attr = (attributes?.onRowDragMove || attributes?.onrowdragmove || "");
    if (attr && rootNamespace && rootNamespace[attr]) {
      const movingNode = event.node, movingNodes = event.nodes, {overNode, overIndex, vDirection} = event;
      const movingData = movingNode.data, overData = overNode?.data;
      // let rowNeedsToMove = false; if (movingData && overData && (movingData._id || movingData.id || movingData.key) !== (overData._id || overData.id || overData.key)) { rowNeedsToMove = true }
      const colState = gridRef?.current?.api.getColumnState() || [], sortActive = colState.some((c) => c.sort), filterActive = gridRef?.current?.api.isAnyFilterPresent();
      const setFilters = gridRef?.current?.api.getFilterModel(), filterKeys = rz.objKeys(setFilters || {}), groupColumns = gridRef?.current?.api.getRowGroupColumns();
      const cellRanges = gridRef?.current?.api.getCellRanges(), checkedNodes = gridRef?.current?.api.getSelectedNodes(), selectedNodes:Array<any> = [];
      if (cellRanges && cellRanges.length === 1 && cellRanges[0].startRow && cellRanges[0].endRow) {
        if (agType.rowModelType === "serverSide") {
          gridRef?.current?.api.forEachNode((node) => {
            // @ts-ignore
            if (!node.group && node.rowIndex >= cellRanges[0].startRow.rowIndex && node.rowIndex <= cellRanges[0].endRow.rowIndex) { selectedNodes.push(node) }
          });
        }
        else {
          gridRef?.current?.api.forEachNodeAfterFilterAndSort((node) => {
            // @ts-ignore
            if (!node.group && node.rowIndex >= cellRanges[0].startRow.rowIndex && node.rowIndex <= cellRanges[0].endRow.rowIndex) { selectedNodes.push(node) }
          });
        }
      }
      // eslint-disable-next-line max-len
      if (fnc.config?.isDev) console.log({action: "<AGgrid key='" + key + "'> -> onRowDragMove()", movingNode, movingNodes, vDirection, overNode, overIndex, movingData, overData, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns});
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        // eslint-disable-next-line max-len
        clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, movingNode, movingNodes, vDirection, overNode, movingData, overData, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns, safe}).then((ret) => {
          if (ret) {
            if (ret?.err) { console.log(ret.err) }
            if (rz.isBoolean(ret?.suppressRowDrag)) { gridRef?.current?.api.setGridOption("suppressRowDrag", ret.suppressRowDrag) }
            else if (rz.isBoolean(agType.suppressRowDrag)) { gridRef?.current?.api.setGridOption("suppressRowDrag", Boolean(agType.suppressRowDrag)) }
            if (ret?.state?.fullPath && ret?.state?.newState) {
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
            }
          }
          gridRef?.current?.api.clearFocusedCell();
        }).catch((err) => { console.log(err) });
      }
      else if (rz.isFunction(clientFNC)) {
        // eslint-disable-next-line max-len
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, movingNode, movingNodes, vDirection, overNode, movingData, overData, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns, safe});
        if (ret) {
          if (ret?.err) { console.log(ret.err) }
          if (rz.isBoolean(ret?.suppressRowDrag)) { gridRef?.current?.api.setGridOption("suppressRowDrag", ret.suppressRowDrag) }
          else if (rz.isBoolean(agType.suppressRowDrag)) { gridRef?.current?.api.setGridOption("suppressRowDrag", Boolean(agType.suppressRowDrag)) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        }
        gridRef?.current?.api.clearFocusedCell();
      }
    }
  }, [attributes, rootNamespace, key, agType.rowModelType, agType.suppressRowDrag, rootAttributes.key, user, lng, dct, dispatch, safe]);

  // ====================================================
  // onRowDragEnd
  // ====================================================
  const onRowDragEnd = useCallback((event) => {
    if (gridRef.current?.api) {
      // Apply changes to Redux-state:
      const data:Array<any> = [];
      gridRef.current?.api?.forEachNode((node) => { data.push(node.data) });
      // @ts-ignore
      dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {data, loading: false, value: null}}));
    }

    const attr = (attributes?.onRowDragEnd || attributes?.onrowdragend || "");
    if (attr && rootNamespace && rootNamespace[attr]) {
      const movingNode = event.node, movingNodes = event.nodes, {overNode, overIndex, vDirection} = event;
      const movingData = movingNode.data, overData = overNode?.data;
      // let rowNeedsToMove = false; if (movingData && overData && (movingData._id || movingData.id || movingData.key) !== (overData._id || overData.id || overData.key)) { rowNeedsToMove = true }
      const colState = gridRef?.current?.api.getColumnState() || [], sortActive = colState.some((c) => c.sort), filterActive = gridRef?.current?.api.isAnyFilterPresent();
      const setFilters = gridRef?.current?.api.getFilterModel(), filterKeys = rz.objKeys(setFilters || {}), groupColumns = gridRef?.current?.api.getRowGroupColumns();
      const cellRanges = gridRef?.current?.api.getCellRanges(), checkedNodes = gridRef?.current?.api.getSelectedNodes(), selectedNodes:Array<any> = [];
      if (cellRanges && cellRanges.length === 1 && cellRanges[0].startRow && cellRanges[0].endRow) {
        const startRow = Math.min(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex), endRow = Math.max(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex);
        if (agType.rowModelType === "serverSide") {
          gridRef?.current?.api.forEachNode((node) => {
            // @ts-ignore
            if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow) { selectedNodes.push(node) }
          });
        }
        else {
          gridRef?.current?.api.forEachNodeAfterFilterAndSort((node) => {
            // @ts-ignore
            if (!node.group && node.rowIndex >= startRow.rondex && node.rowIndex <= endRow) { selectedNodes.push(node) }
          });
        }
      }
      // eslint-disable-next-line max-len
      if (fnc.config?.isDev) console.log({action: "<AGgrid key='" + key + "'> -> onRowDragEnd()", movingNode, movingNodes, vDirection, overNode, overIndex, movingData, overData, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns});
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        // eslint-disable-next-line max-len
        clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, movingNode, overNode, movingNodes, vDirection, overIndex, movingData, overData, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns, safe}).then((ret) => {
          if (ret) {
            if (ret?.err) { console.log(ret.err) }
            if (ret?.cancel) { gridRef?.current?.api.clearFocusedCell() }
            if (rz.isBoolean(ret?.suppressRowDrag)) { gridRef?.current?.api.setGridOption("suppressRowDrag", ret.suppressRowDrag) }
            else if (rz.isBoolean(agType.suppressRowDrag)) { gridRef?.current?.api.setGridOption("suppressRowDrag", Boolean(agType.suppressRowDrag)) }
            if (ret?.state?.fullPath && ret?.state?.newState) {
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
            }
          }
          gridRef?.current?.api.clearFocusedCell();
        }).catch((err) => { console.log(err) });
      }
      else if (rz.isFunction(clientFNC)) {
        // eslint-disable-next-line max-len
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, movingNode, overNode, movingNodes, vDirection, overIndex, movingData, overData, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns, safe});
        if (ret) {
          if (ret?.err) { console.log(ret.err) }
          if (ret?.cancel) { gridRef?.current?.api.clearFocusedCell() }
          if (rz.isBoolean(ret?.suppressRowDrag)) { gridRef?.current?.api.setGridOption("suppressRowDrag", ret.suppressRowDrag) }
          else if (rz.isBoolean(agType.suppressRowDrag)) { gridRef?.current?.api.setGridOption("suppressRowDrag", Boolean(agType.suppressRowDrag)) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        }
        gridRef?.current?.api.clearFocusedCell();
      }
    }
  }, [attributes, rootNamespace, dispatch, rootAttributes.key, fullPath, key, agType.rowModelType, agType.suppressRowDrag, user, lng, dct, safe]);

  // ====================================================
  // onRowDragLeave
  // ====================================================
  const onRowDragLeave = useCallback((event) => {
    const attr = (attributes?.onRowDragLeave || attributes?.onrowdragleave || "");
    if (attr && rootNamespace && rootNamespace[attr]) {
      const movingNode = event.node, movingNodes = event.nodes, movingData = movingNode.data, {vDirection, overIndex} = event;
      const colState = gridRef?.current?.api.getColumnState() || [], sortActive = colState.some((c) => c.sort), filterActive = gridRef?.current?.api.isAnyFilterPresent();
      const setFilters = gridRef?.current?.api.getFilterModel(), filterKeys = rz.objKeys(setFilters || {}), groupColumns = gridRef?.current?.api.getRowGroupColumns();
      const cellRanges = gridRef?.current?.api.getCellRanges(), checkedNodes = gridRef?.current?.api.getSelectedNodes(), selectedNodes:Array<any> = [];
      if (cellRanges && cellRanges.length === 1 && cellRanges[0].startRow && cellRanges[0].endRow) {
        const startRow = Math.min(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex), endRow = Math.max(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex);
        if (agType.rowModelType === "serverSide") {
          gridRef?.current?.api.forEachNode((node) => {
            // @ts-ignore
            if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow) { selectedNodes.push(node) }
          });
        }
        else {
          gridRef?.current?.api.forEachNodeAfterFilterAndSort((node) => {
            // @ts-ignore
            if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow) { selectedNodes.push(node) }
          });
        }
      }
      // eslint-disable-next-line max-len
      if (fnc.config?.isDev) console.log({action: "<AGgrid key='" + key + "'> -> onRowDragLeave()", movingNode, movingNodes, movingData, overIndex, vDirection, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns});
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        // eslint-disable-next-line max-len
        clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, movingNode, movingNodes, movingData, overIndex, vDirection, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns, safe}).then((ret) => {
          if (ret) {
            if (ret?.err) { console.log(ret.err) }
            if (ret?.cancel) { gridRef?.current?.api.clearFocusedCell() }
            if (rz.isBoolean(ret?.suppressRowDrag)) { gridRef?.current?.api.setGridOption("suppressRowDrag", ret.suppressRowDrag) }
            else if (rz.isBoolean(agType.suppressRowDrag)) { gridRef?.current?.api.setGridOption("suppressRowDrag", Boolean(agType.suppressRowDrag)) }
            if (ret?.state?.fullPath && ret?.state?.newState) {
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
            }
          }
          gridRef?.current?.api.clearFocusedCell();
        }).catch((err) => { console.log(err) });
      }
      else if (rz.isFunction(clientFNC)) {
        // eslint-disable-next-line max-len
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, movingNode, movingNodes, movingData, overIndex, vDirection, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns, safe});
        if (ret) {
          if (ret?.err) { console.log(ret.err) }
          if (ret?.cancel) { gridRef?.current?.api.clearFocusedCell() }
          if (rz.isBoolean(ret?.suppressRowDrag)) { gridRef?.current?.api.setGridOption("suppressRowDrag", ret.suppressRowDrag) }
          else if (rz.isBoolean(agType.suppressRowDrag)) { gridRef?.current?.api.setGridOption("suppressRowDrag", Boolean(agType.suppressRowDrag)) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        }
        gridRef?.current?.api.clearFocusedCell();
      }
    }
  }, [attributes, rootNamespace, key, agType.rowModelType, agType.suppressRowDrag, rootAttributes.key, user, lng, dct, dispatch, safe]);

  // ====================================================
  // onSortChanged
  // ====================================================
  const onSortChanged = useCallback(() => {
    const attr = (attributes?.onSortChanged || attributes?.onsortchanged || "");
    if (attr && rootNamespace && rootNamespace[attr]) {
      const colState = gridRef?.current?.api.getColumnState() || [], sortActive = colState.some((c) => c.sort), filterActive = gridRef?.current?.api.isAnyFilterPresent();
      const setFilters = gridRef?.current?.api.getFilterModel(), filterKeys = rz.objKeys(setFilters || {}), groupColumns = gridRef?.current?.api.getRowGroupColumns();
      const cellRanges = gridRef?.current?.api.getCellRanges(), checkedNodes = gridRef?.current?.api.getSelectedNodes(), selectedNodes:Array<any> = [];
      if (cellRanges && cellRanges.length === 1 && cellRanges[0].startRow && cellRanges[0].endRow) {
        const startRow = Math.min(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex), endRow = Math.max(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex);
        if (agType.rowModelType === "serverSide") {
          gridRef?.current?.api.forEachNode((node) => {
            // @ts-ignore
            if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow) { selectedNodes.push(node) }
          });
        }
        else {
          gridRef?.current?.api.forEachNodeAfterFilterAndSort((node) => {
            // @ts-ignore
            if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow) { selectedNodes.push(node) }
          });
        }
      }
      if (fnc.config?.isDev) console.log({action: "<AGgrid key='" + key + "'> -> onSortChanged()", cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns});
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns, safe}).then((ret) => {
          if (ret) {
            if (ret?.err) { console.log(ret.err) }
            if (ret?.state?.fullPath && ret?.state?.newState) {
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
            }
          }
        }).catch((err) => { console.log(err) });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns, safe});
        if (ret) {
          if (ret?.err) { console.log(ret.err) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        }
      }
    }
  }, [attributes, rootNamespace, key, agType.rowModelType, rootAttributes.key, user, lng, dct, dispatch, safe]);

  // ====================================================
  // onFilterChanged
  // ====================================================
  const onFilterChanged = useCallback(() => {
    const attr = (attributes?.onFilterChanged || attributes?.onfilterchanged || "");
    if (attr && rootNamespace && rootNamespace[attr]) {
      const colState = gridRef?.current?.api.getColumnState() || [], sortActive = colState.some((c) => c.sort), filterActive = gridRef?.current?.api.isAnyFilterPresent();
      const setFilters = gridRef?.current?.api.getFilterModel(), filterKeys = rz.objKeys(setFilters || {}), groupColumns = gridRef?.current?.api.getRowGroupColumns();
      const cellRanges = gridRef?.current?.api.getCellRanges(), checkedNodes = gridRef?.current?.api.getSelectedNodes(), selectedNodes:Array<any> = [];
      if (cellRanges && cellRanges.length === 1 && cellRanges[0].startRow && cellRanges[0].endRow) {
        const startRow = Math.min(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex), endRow = Math.max(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex);
        if (agType.rowModelType === "serverSide") {
          gridRef?.current?.api.forEachNode((node) => {
            // @ts-ignore
            if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow) { selectedNodes.push(node) }
          });
        }
        else {
          gridRef?.current?.api.forEachNodeAfterFilterAndSort((node) => {
            // @ts-ignore
            if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow) { selectedNodes.push(node) }
          });
        }
      }
      if (fnc.config?.isDev) console.log({action: "<AGgrid key='" + key + "'> -> onFilterChanged()", cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns});
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns, safe}).then((ret) => {
          if (ret) {
            if (ret?.err) { console.log(ret.err) }
            if (ret?.state?.fullPath && ret?.state?.newState) {
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
            }
          }
        }).catch((err) => { console.log(err) });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns, safe});
        if (ret) {
          if (ret?.err) { console.log(ret.err) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        }
      }
    }
  }, [attributes, rootNamespace, key, agType.rowModelType, rootAttributes.key, user, lng, dct, dispatch, safe]);

  // ====================================================
  // onColumnRowGroupChanged
  // ====================================================
  const onColumnRowGroupChanged = useCallback(() => {
    const attr = (attributes?.onColumnRowGroupChanged || attributes?.oncolumnrowgroupchanged || "");
    if (attr && rootNamespace && rootNamespace[attr]) {
      const colState = gridRef?.current?.api.getColumnState() || [], sortActive = colState.some((c) => c.sort), filterActive = gridRef?.current?.api.isAnyFilterPresent();
      const setFilters = gridRef?.current?.api.getFilterModel(), filterKeys = rz.objKeys(setFilters || {}), groupColumns = gridRef?.current?.api.getRowGroupColumns();
      const cellRanges = gridRef?.current?.api.getCellRanges(), checkedNodes = gridRef?.current?.api.getSelectedNodes(), selectedNodes:Array<any> = [];
      if (cellRanges && cellRanges.length === 1 && cellRanges[0].startRow && cellRanges[0].endRow) {
        const startRow = Math.min(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex), endRow = Math.max(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex);
        if (agType.rowModelType === "serverSide") {
          gridRef?.current?.api.forEachNode((node) => {
            // @ts-ignore
            if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow) { selectedNodes.push(node) }
          });
        }
        else {
          gridRef?.current?.api.forEachNodeAfterFilterAndSort((node) => {
            // @ts-ignore
            if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow) { selectedNodes.push(node) }
          });
        }
      }
      if (fnc.config?.isDev) console.log({action: "<AGgrid key='" + key + "'> -> onColumnRowGroupChanged()", cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns});
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns, safe}).then((ret) => {
          if (ret) {
            if (ret?.err) { console.log(ret.err) }
            if (ret?.state?.fullPath && ret?.state?.newState) {
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
            }
          }
        }).catch((err) => { console.log(err) });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns, safe});
        if (ret) {
          if (ret?.err) { console.log(ret.err) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        }
      }
    }
  }, [attributes, rootNamespace, key, agType.rowModelType, rootAttributes.key, user, lng, dct, dispatch, safe]);

  // ====================================================
  // onDisplayedColumnsChanged
  // ====================================================
  const onDisplayedColumnsChanged = useCallback(() => {
    const attr = (attributes?.onDisplayedColumnsChanged || attributes?.ondisplayedcolumnschanged || "");
    if (attr && rootNamespace && rootNamespace[attr]) {
      const colState = gridRef?.current?.api.getColumnState() || [], sortActive = colState.some((c) => c.sort), filterActive = gridRef?.current?.api.isAnyFilterPresent();
      const setFilters = gridRef?.current?.api.getFilterModel(), filterKeys = rz.objKeys(setFilters || {}), groupColumns = gridRef?.current?.api.getRowGroupColumns();
      const cellRanges = gridRef?.current?.api.getCellRanges(), checkedNodes = gridRef?.current?.api.getSelectedNodes(), selectedNodes:Array<any> = [];
      if (cellRanges && cellRanges.length === 1 && cellRanges[0].startRow && cellRanges[0].endRow) {
        const startRow = Math.min(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex), endRow = Math.max(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex);
        if (agType.rowModelType === "serverSide") {
          gridRef?.current?.api.forEachNode((node) => {
            // @ts-ignore
            if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow) { selectedNodes.push(node) }
          });
        }
        else {
          gridRef?.current?.api.forEachNodeAfterFilterAndSort((node) => {
            // @ts-ignore
            if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow) { selectedNodes.push(node) }
          });
        }
      }

      if (fnc.config?.isDev) console.log({action: "<AGgrid key='" + key + "'> -> onDisplayedColumnsChanged()", cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns});
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns, safe}).then((ret) => {
          if (ret) {
            if (ret?.err) { console.log(ret.err) }
            if (ret?.state?.fullPath && ret?.state?.newState) {
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
            }
          }
        }).catch((err) => { console.log(err) });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns, safe});
        if (ret) {
          if (ret?.err) { console.log(ret.err) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        }
      }
    }
  }, [attributes, rootNamespace, key, agType.rowModelType, rootAttributes.key, user, lng, dct, dispatch, safe]);

  // ====================================================
  // Grid onRowDataUpdated
  // ====================================================
  const onRowDataUpdated = useCallback((params) => {
    if (gridRef && gridRef.current && gridRef.current.api) {
      const attr = (attributes?.onRowDataUpdated || attributes?.onrowdataupdated || "");
      if (attr) {
        if (fnc.config?.isDev) console.log({action: "<AGgrid key='" + key + "'> -> onRowDataUpdated()", params});
        const colState = gridRef?.current?.api.getColumnState() || [], sortActive = colState.some((c) => c.sort), filterActive = gridRef?.current?.api.isAnyFilterPresent();

        // Apply changes to AGgrid:
        const clientFNC = rootNamespace[attr];
        if (rz.isPromise(clientFNC)) {
          clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef!.current, colState, sortActive, filterActive, lng, dct, safe}).then((ret) => {
            if (ret) {
              if (ret?.err) { console.log(ret.err) }
              if (ret?.state?.fullPath && ret?.state?.newState) {
                dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
              }
            }
          });
        }
        else if (rz.isFunction(clientFNC)) {
          const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef!.current, colState, sortActive, filterActive, lng, dct, safe});
          if (ret) {
            if (ret?.err) { console.log(ret.err) }
            if (ret?.state?.fullPath && ret?.state?.newState) {
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
            }
          }
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributes, dispatch, key, lng, rootAttributes.key, rootNamespace, user?.login, safe]);

  // ====================================================
  // Grid onCellSelectionChanged
  // ====================================================
  const onCellSelectionChanged = useCallback((params) => {
    if (gridRef && gridRef.current && gridRef.current.api) {
      const attr = (attributes?.onCellSelectionChanged || attributes?.oncellselectionchanged || attributes?.onSelectionChanged || attributes?.onselectionchanged || "");
      if (attr) {
        if (fnc.config?.isDev) console.log({action: "<AGgrid key='" + key + "'> -> onCellSelectionChanged()", params});
        const colState = gridRef?.current?.api.getColumnState() || [], sortActive = colState.some((c) => c.sort), filterActive = gridRef?.current?.api.isAnyFilterPresent();
        const setFilters = gridRef?.current?.api.getFilterModel(), filterKeys = rz.objKeys(setFilters || {}), groupColumns = gridRef?.current?.api.getRowGroupColumns();
        const cellRanges = gridRef?.current?.api.getCellRanges(), checkedNodes = gridRef?.current?.api.getSelectedNodes(), selectedNodes:Array<any> = [];
        if (cellRanges && cellRanges.length === 1 && cellRanges[0].startRow && cellRanges[0].endRow) {
          const startRow = Math.min(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex), endRow = Math.max(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex);
          if (agType.rowModelType === "serverSide") {
            gridRef?.current?.api.forEachNode((node) => {
              // @ts-ignore
              if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow) { selectedNodes.push(node) }
            });
          }
          else {
            gridRef?.current?.api.forEachNodeAfterFilterAndSort((node) => {
              // @ts-ignore
              if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow) { selectedNodes.push(node) }
            });
          }
        }

        // Apply changes to AGgrid:
        const clientFNC = rootNamespace[attr];
        if (rz.isPromise(clientFNC)) {
          clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef!.current, lng, dct, params, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns, safe}).then((ret) => {
            if (ret) {
              if (ret?.err) { console.log(ret.err) }
              if (ret?.state?.fullPath && ret?.state?.newState) {
                dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
              }
            }
          });
        }
        else if (rz.isFunction(clientFNC)) {
          const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef!.current, lng, dct, params, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns, safe});
          if (ret) {
            if (ret?.err) { console.log(ret.err) }
            if (ret?.state?.fullPath && ret?.state?.newState) {
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
            }
          }
        }
      }
    }
  }, [agType.rowModelType, attributes, dct, dispatch, key, lng, rootAttributes.key, rootNamespace, user, safe]);

  // ====================================================
  // Grid onRowSelected
  // ====================================================
  const onRowSelected = useCallback((params) => {
    if (gridRef && gridRef.current && gridRef.current.api) {
      const attr = (attributes?.onRowSelected || attributes?.onrowselected);
      if (attr) {
        if (fnc.config?.isDev) console.log({action: "<AGgrid key='" + key + "'> -> onRowSelected()", params});
        const colState = gridRef?.current?.api.getColumnState() || [], sortActive = colState.some((c) => c.sort), filterActive = gridRef?.current?.api.isAnyFilterPresent();
        const setFilters = gridRef?.current?.api.getFilterModel(), filterKeys = rz.objKeys(setFilters || {}), groupColumns = gridRef?.current?.api.getRowGroupColumns();
        const cellRanges = gridRef?.current?.api.getCellRanges(), checkedNodes = gridRef?.current?.api.getSelectedNodes(), selectedNodes:Array<any> = [];
        if (cellRanges && cellRanges.length === 1 && cellRanges[0].startRow && cellRanges[0].endRow) {
          const startRow = Math.min(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex), endRow = Math.max(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex);
          if (agType.rowModelType === "serverSide") {
            gridRef?.current?.api.forEachNode((node) => {
              // @ts-ignore
              if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow) { selectedNodes.push(node) }
            });
          }
          else {
            gridRef?.current?.api.forEachNodeAfterFilterAndSort((node) => {
              // @ts-ignore
              if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow) { selectedNodes.push(node) }
            });
          }
        }

        // Apply changes to AGgrid:
        const clientFNC = rootNamespace[attr];
        if (rz.isPromise(clientFNC)) {
          clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef!.current, lng, dct, params, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns, safe}).then((ret) => {
            if (ret) {
              if (ret?.err) { console.log(ret.err) }
              if (ret?.state?.fullPath && ret?.state?.newState) {
                dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
              }
            }
          });
        }
        else if (rz.isFunction(clientFNC)) {
          const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef!.current, lng, dct, params, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns, safe});
          if (ret) {
            if (ret?.err) { console.log(ret.err) }
            if (ret?.state?.fullPath && ret?.state?.newState) {
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
            }
          }
        }
      }
    }
  }, [agType.rowModelType, attributes, dct, dispatch, key, lng, rootAttributes.key, rootNamespace, user, safe]);

  // ====================================================
  // Grid gridDragOver
  // ====================================================
  const gridDragOver = useCallback((event) => {
    if (gridRef && gridRef.current && gridRef.current.api && event && event.dataTransfer && event.dataTransfer.types && event.dataTransfer.types.length) {
      const attr = (attributes?.gridDragOver || attributes?.griddragover);
      if (attr) {
        event.dataTransfer.dropEffect = "copy";
        event.preventDefault();

        if (attr === "true") { return }

        if (fnc.config?.isDev) console.log({action: "<AGgrid key='" + key + "'> -> gridDragOver()", event});
        const colState = gridRef?.current?.api.getColumnState() || [], sortActive = colState.some((c) => c.sort), filterActive = gridRef?.current?.api.isAnyFilterPresent();
        const setFilters = gridRef?.current?.api.getFilterModel(), filterKeys = rz.objKeys(setFilters || {}), groupColumns = gridRef?.current?.api.getRowGroupColumns();
        const cellRanges = gridRef?.current?.api.getCellRanges(), checkedNodes = gridRef?.current?.api.getSelectedNodes(), selectedNodes:Array<any> = [];
        if (cellRanges && cellRanges.length === 1 && cellRanges[0].startRow && cellRanges[0].endRow) {
          const startRow = Math.min(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex), endRow = Math.max(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex);
          if (agType.rowModelType === "serverSide") {
            gridRef?.current?.api.forEachNode((node) => {
              // @ts-ignore
              if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow) { selectedNodes.push(node) }
            });
          }
          else {
            gridRef?.current?.api.forEachNodeAfterFilterAndSort((node) => {
              // @ts-ignore
              if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow) { selectedNodes.push(node) }
            });
          }
        }

        // Apply changes to AGgrid:
        const clientFNC = rootNamespace[attr];
        if (rz.isPromise(clientFNC)) {
          clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef!.current, lng, dct, event, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns, safe}).then((ret) => {
            if (ret) {
              if (ret?.err) { console.log(ret.err) }
              if (ret?.state?.fullPath && ret?.state?.newState) {
                dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
              }
            }
          });
        }
        else if (rz.isFunction(clientFNC)) {
          const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef!.current, lng, dct, event, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns, safe});
          if (ret) {
            if (ret?.err) { console.log(ret.err) }
            if (ret?.state?.fullPath && ret?.state?.newState) {
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
            }
          }
        }
      }
    }
  }, [agType.rowModelType, attributes, dct, dispatch, key, lng, rootAttributes.key, rootNamespace, user, safe]);

  // ====================================================
  // Grid gridDrop
  // ====================================================
  const gridDrop = useCallback((event) => {
    if (gridRef && gridRef.current && gridRef.current.api && event && event.dataTransfer && event.dataTransfer.getData) {
      const jsonData = event.dataTransfer.getData("application/json"), data = rz.objFromJSON(jsonData);
      event.preventDefault();

      const attr = (attributes?.gridDrop || attributes?.griddrop);
      if (attr && data && data._id) {
        if (fnc.config?.isDev) console.log({action: "<AGgrid key='" + key + "'> -> gridDrop()", event, data});
        const colState = gridRef?.current?.api.getColumnState() || [], sortActive = colState.some((c) => c.sort), filterActive = gridRef?.current?.api.isAnyFilterPresent();
        const setFilters = gridRef?.current?.api.getFilterModel(), filterKeys = rz.objKeys(setFilters || {}), groupColumns = gridRef?.current?.api.getRowGroupColumns();
        const cellRanges = gridRef?.current?.api.getCellRanges(), checkedNodes = gridRef?.current?.api.getSelectedNodes(), selectedNodes:Array<any> = [];
        if (cellRanges && cellRanges.length === 1 && cellRanges[0].startRow && cellRanges[0].endRow) {
          const startRow = Math.min(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex), endRow = Math.max(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex);
          if (agType.rowModelType === "serverSide") {
            gridRef?.current?.api.forEachNode((node) => {
              // @ts-ignore
              if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow) { selectedNodes.push(node) }
            });
          }
          else {
            gridRef?.current?.api.forEachNodeAfterFilterAndSort((node) => {
              // @ts-ignore
              if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow) { selectedNodes.push(node) }
            });
          }
        }

        // Apply changes to AGgrid:
        const clientFNC = rootNamespace[attr];
        if (rz.isPromise(clientFNC)) {
          // eslint-disable-next-line max-len
          clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef!.current, lng, dct, event, data, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns, safe}).then((ret) => {
            if (ret) {
              if (ret?.err) { console.log(ret.err) }
              if (ret?.state?.fullPath && ret?.state?.newState) {
                dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
              }
            }
          });
        }
        else if (rz.isFunction(clientFNC)) {
          // eslint-disable-next-line max-len
          const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef!.current, lng, dct, event, data, cellRanges, checkedNodes, selectedNodes, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns, safe});
          if (ret) {
            if (ret?.err) { console.log(ret.err) }
            if (ret?.state?.fullPath && ret?.state?.newState) {
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
            }
          }
        }
      }
    }
  }, [agType.rowModelType, attributes, dct, dispatch, key, lng, rootAttributes.key, rootNamespace, user, safe]);

  // ====================================================
  // onCellKeyDown
  // ====================================================
  const onCellKeyDown = useCallback((params) => {
    if (gridRef && gridRef.current && gridRef.current.api) {
      const attr = (attributes?.onCellKeyDown || attributes?.oncellkeydown || "");
      if (attr) {
        // if (fnc.config?.isDev) console.log({action: "<AGgrid key='" + key + "'> -> onCellKeyDown()", params});
        const colState = gridRef?.current?.api.getColumnState() || [], sortActive = colState.some((c) => c.sort), filterActive = gridRef?.current?.api.isAnyFilterPresent();

        // Apply changes to AGgrid:
        const clientFNC = rootNamespace[attr];
        if (rz.isFunction(clientFNC)) {
          const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef!.current, colState, sortActive, filterActive, lng, dct, params, safe});
          if (ret) {
            if (ret?.err) { console.log(ret.err) }
            if (ret?.state?.fullPath && ret?.state?.newState) {
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
            }
            if (ret.preventDefault) { params.event.preventDefault() }
          }
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributes, dispatch, key, lng, rootAttributes.key, rootNamespace, user?.login, safe]);

  // ====================================================
  // sendToClipboard
  // ====================================================
  const sendToClipboard = useCallback((params) => {
    if (gridRef && gridRef.current && gridRef.current.api) {
      const attr = attributes?.sendToClipboard;
      if (attr) {
        if (fnc.config?.isDev) console.log({action: "<AGgrid key='" + key + "'> -> sendToClipboard()", params});
        const colState = gridRef?.current?.api.getColumnState() || [], sortActive = colState.some((c) => c.sort), filterActive = gridRef?.current?.api.isAnyFilterPresent();
        const setFilters = gridRef?.current?.api.getFilterModel(), filterKeys = rz.objKeys(setFilters || {}), groupColumns = gridRef?.current?.api.getRowGroupColumns();
        const cellRanges = gridRef?.current?.api.getCellRanges(), checkedNodes = gridRef?.current?.api.getSelectedNodes(), selectedNodes:Array<any> = [];
        if (cellRanges && cellRanges.length === 1 && cellRanges[0].startRow && cellRanges[0].endRow) {
          const startRow = Math.min(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex), endRow = Math.max(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex);
          if (agType.rowModelType === "serverSide") {
            gridRef?.current?.api.forEachNode((node) => {
              // @ts-ignore
              if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow) { selectedNodes.push(node) }
            });
          }
          else {
            gridRef?.current?.api.forEachNodeAfterFilterAndSort((node) => {
              // @ts-ignore
              if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow) { selectedNodes.push(node) }
            });
          }
        }

        // Apply changes to AGgrid:
        const clientFNC = rootNamespace[attr];
        if (rz.isFunction(clientFNC)) {
          const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef!.current, colState, sortActive, filterActive, setFilters, filterKeys, groupColumns, cellRanges, checkedNodes, selectedNodes, lng, dct, params, safe});
          if (ret) {
            if (ret?.err) { console.log(ret.err) }
            if (ret?.state?.fullPath && ret?.state?.newState) {
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
            }
            // @ts-ignore
            if (ret.preventCopy === true) { navigator.clipboard.writeText("") }
            else if (ret.text) {
              // @ts-ignore
              navigator.clipboard.writeText(ret.text).catch((err) => {
                console.log(err);
                // Fallback для старых браузеров
                const textArea = document.createElement("textarea");
                textArea.value = ret.text;
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand("copy");
                document.body.removeChild(textArea);
              });
            }
            else if (ret.preventCopy === false) {
              // @ts-ignore
              navigator.clipboard.writeText(params.data || "").catch((err) => {
                console.log(err);
                // Fallback для старых браузеров
                const textArea = document.createElement("textarea");
                textArea.value = (params.data || "");
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand("copy");
                document.body.removeChild(textArea);
              });
            }
          }
        }
      }
      else {
        // gridRef.current.api.copySelectedRangeToClipboard(); // Вызывает баг бесконечного цикла копирования ячейки
      }
    }
  }, [agType.rowModelType, attributes, dct, dispatch, key, lng, rootAttributes.key, rootNamespace, user, safe]);

  // ====================================================
  // Grid ready
  // ====================================================
  const onGridReady = useCallback((params) => {
    if (gridRef && gridRef.current && gridRef.current.api) {
      if (fnc.config?.isDev) console.log({action: "<AGgrid key='" + key + "'> -> onGridReady()"});
      setGridReady(true); // Change state -> Auto-height AGgrid & Auto-width columns
      const attr = (attributes?.onGridReady || attributes?.ongridready || "");
      if (attr) {
        const colState = gridRef?.current?.api.getColumnState() || [], sortActive = colState.some((c) => c.sort), filterActive = gridRef?.current?.api.isAnyFilterPresent();

        const clientFNC = rootNamespace[attr];
        if (rz.isPromise(clientFNC)) {
          clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef!.current, colState, sortActive, filterActive, lng, dct, safe}).then((ret) => {
            if (ret) {
              if (ret?.err) { console.log(ret.err) }
              if (ret?.state?.fullPath && ret?.state?.newState) {
                dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
              }
            }
          });
        }
        else if (rz.isFunction(clientFNC)) {
          const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef!.current, colState, sortActive, filterActive, lng, dct, safe});
          if (ret) {
            if (ret?.err) { console.log(ret.err) }
            if (ret?.state?.fullPath && ret?.state?.newState) {
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
            }
          }
        }
      }
    }
  }, [key, attributes, rootNamespace, rootAttributes.key, user, lng, dct, dispatch, safe]);

  // ====================================================
  // Configure rows
  // ====================================================
  useEffect(() => {
    if (!gridReady || !gridRef.current?.api) { return }

    // The first prioriry - set initial rows from redux state:
    if (elState.value && rz.isArray(elState.value)) { // Set Rows Force from ReduxState using elState.value
      // Apply changes to AGgrid:
      const mrows = createRowsID(elState.value);
      if (agType.rowModelType === "serverSide") {
        gridRef.current?.api?.applyServerSideRowData({successParams: {rowData: mrows, rowCount: mrows.length + 1}}); // mrows.length + 1 - need to load additional rows when scrolling
      }
      else {
        gridRef.current?.api?.setGridOption("rowData", mrows);
      }
      // Apply changes to state:
      // @ts-ignore
      dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {data: mrows, loading: false, value: null}}));
      if (fnc.config?.isDev) {
        console.log("<AgGrid key='" + key + "'> -> onLoadRowsData('" + rootAttributes.key + "'): Loaded " + mrows.length + " rows");
        // console.log(jsn[okeys[0]]);
      }
      if (agType.rowModelType !== "serverSide") { return }
    }

    // 2025-04-05: SSRM
    if (agType.rowModelType === "serverSide") {
      const attr1 = (attributes?.serverSideDatasource || attributes?.serversidedatasource || attributes?.dataSource || attributes?.datasource);
      const attr2 = (attributes?.rowData || attributes?.rowdata || attributes?.rows); // The most common option
      if ((!attr1 && !attr2) || !elState.loading) { return } // Rows are already loaded. Cancel duplicate loading

      if (attr1 && rootNamespace && rootNamespace[attr1]) {
        const clientFNC = rootNamespace[attr1];
        if (rz.isPromise(clientFNC)) {
          clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, safe}).then((ret) => {
            if (ret && ret.getRows) {
              // Apply changes to AGgrid:
              gridRef.current?.api?.setGridOption("serverSideDatasource", ret);
              // @ts-ignore
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {loading: false, value: null}}));
            }
          }).catch((err) => { console.log(err) });
        }
        else if (rz.isFunction(clientFNC)) {
          const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, safe});
          if (ret && ret.getRows) {
            // Apply changes to AGgrid:
            gridRef.current?.api?.setGridOption("serverSideDatasource", ret);
            // @ts-ignore
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {loading: false, value: null}}));
          }
        }
      }
      else if (attr2 && rootNamespace && rootNamespace[attr2]) { // The most common option
        // Middle function:
        const extendRowsState = (rData:Array<{[key:string]:any}>) => {
          const mrows = createRowsID(rData);
          const nrows = (elState.data || []).map((rr) => ({...rr}));
          mrows.forEach((mm) => {
            if (mm._id) {
              const i = rz.arrFindObj(nrows, {_id: mm._id});
              if (~i) { nrows[i] = {...mm} } // Replace existing row
              else { nrows.push({...mm}) } // Append a new row
            }
          });
          return nrows;
        };

        const serverAns = {
          getRows: (params) => {
            const clientFNC = rootNamespace[attr2];
            if (rz.isPromise(clientFNC)) {
              clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, params, safe}).then((ret) => {
                if (ret && ret.rowData) {
                  params.success(ret);
                  // Apply changes to state:
                  // @ts-ignore
                  dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {data: extendRowsState(ret.rowData), loading: false, value: null}}));
                  if (fnc.config?.isDev) {
                    console.log("<AgGrid key='" + key + "'> -> onLoadRowsData('" + rootAttributes.key + "'): Loaded " + ret.rowData.length + " rows");
                  }
                }
                else {
                  params.fail();
                  // @ts-ignore
                  dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {loading: false, value: null}}));
                }
              }).catch((err) => { console.log(err) });
            }
            else if (rz.isFunction(clientFNC)) {
              const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, params, safe});
              if (ret && ret.rowData) {
                params.success(ret);
                // Apply changes to state:
                // @ts-ignore
                dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {data: extendRowsState(ret.rowData), loading: false, value: null}}));
                if (fnc.config?.isDev) {
                  console.log("<AgGrid key='" + key + "'> -> onLoadRowsData('" + rootAttributes.key + "'): Loaded " + ret.rowData.length + " rows");
                }
              }
              else {
                params.fail();
                // @ts-ignore
                dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {loading: false, value: null}}));
              }
            }
          }
        };
        gridRef.current?.api?.setGridOption("serverSideDatasource", serverAns);
      }
    }
    else {
      // CSRM
      const attr = (attributes?.rowData || attributes?.rowdata || attributes?.rows || "");
      if (!attr || !elState.loading) { return } // Rows are already loaded. Cancel duplicate loading

      if (attr.startsWith("{") && attr.endsWith("}")) { // Fast Loading Rows from JSON attribute <aggrid rowData="{'rowData':[...]}" />
        const jsn = rz.objFromJSON((rz.strReplaceAll(attr, "'", "\"")).trim());
        if (jsn) {
          const okeys = rz.objKeys(jsn);
          if (okeys[0] && rz.isArray(jsn[okeys[0]])) {
            const mrows = createRowsID(jsn[okeys[0]]);
            // Apply changes to AGgrid:
            gridRef.current?.api?.setGridOption("rowData", mrows);
            // Apply changes to state:
            // @ts-ignore
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {data: mrows, loading: false, value: null}}));
            if (fnc.config?.isDev) {
              console.log("<AgGrid key='" + key + "'> -> onLoadRowsData('" + rootAttributes.key + "'): Loaded " + mrows.length + " rows");
              // console.log(jsn[okeys[0]]);
            }
          }
        }
      }
      else if (attr && rootNamespace && rootNamespace[attr]) { // Lasy Loading Rows using user function - window[appkey].rowData<Promise>(gridOptions)
        // elState.loading !!! After this will be called - elState.loading = false => Prevent double calling this
        // if You want to load data again - setState(elState.loading = true)
        const clientFNC = rootNamespace[attr];
        if (rz.isPromise(clientFNC)) {
          clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, safe}).then((ret) => {
            if (ret && rz.isArray(ret)) {
              // setRowData(ret);
              const mrows = createRowsID(ret);
              // Apply changes to AGgrid:
              gridRef.current?.api?.setGridOption("rowData", mrows);
              // Apply changes to state:
              // @ts-ignore
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {data: mrows, loading: false, value: null}}));
              if (fnc.config?.isDev) {
                console.log("<AgGrid key='" + key + "'> -> onLoadRowsData('" + rootAttributes.key + "'): Loaded " + mrows.length + " rows");
              }
            }
          }).catch((err) => { console.log(err) });
        }
        else if (rz.isFunction(clientFNC)) {
          const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, safe});
          if (ret && rz.isArray(ret)) {
            const mrows = createRowsID(ret);
            // Apply changes to AGgrid:
            gridRef.current?.api?.setGridOption("rowData", mrows);
            // Apply changes to state:
            // @ts-ignore
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {data: mrows, loading: false, value: null}}));
            if (fnc.config?.isDev) {
              console.log("<AgGrid key='" + key + "'> -> onLoadRowsData('" + rootAttributes.key + "'): Loaded " + mrows.length + " rows");
            }
          }
        }
      }
    }
  }, [attributes, key, rootAttributes.key, rootNamespace, user.login, dispatch, fullPath, gridReady, elState.loading, elState.value, elState.data, user, dct, lng, agType.rowModelType, safe]);

  // ====================================================
  // Grid onFirstDataRendered
  // ====================================================
  const onFirstDataRendered = useCallback((params) => {
    if (gridRef && gridRef.current && gridRef.current.api) {
      if (fnc.config?.isDev) console.log({action: "<AGgrid key='" + key + "'> -> onFirstDataRendered()"});
      setGridFirstDataRenderred(true); // Change state -> Auto-height AGgrid & Auto-width columns
      const attr = (attributes?.onFirstDataRendered || attributes?.onfirstdatarendered || "");
      if (attr) {
        const colState = gridRef?.current?.api.getColumnState() || [], sortActive = colState.some((c) => c.sort), filterActive = gridRef?.current?.api.isAnyFilterPresent();

        const clientFNC = rootNamespace[attr];
        if (rz.isPromise(clientFNC)) {
          clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef!.current, colState, sortActive, filterActive, lng, dct, safe}).then((ret) => {
            if (ret) {
              if (ret?.err) { console.log(ret.err) }
              if (ret?.state?.fullPath && ret?.state?.newState) {
                dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
              }
            }
          });
        }
        else if (rz.isFunction(clientFNC)) {
          const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef!.current, colState, sortActive, filterActive, lng, dct, safe});
          if (ret) {
            if (ret?.err) { console.log(ret.err) }
            if (ret?.state?.fullPath && ret?.state?.newState) {
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
            }
          }
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributes, dispatch, key, lng, rootAttributes.key, rootNamespace, user?.login, safe]);

  // ====================================================
  // Auto-size columns-width after gridFirstDataRenderred
  // ====================================================
  /* gridRef.current.api.sizeColumnsToFit({
    defaultMinWidth: 100,
    columnLimits: [{ key: 'country', minWidth: 900 }],
  }); */
  useEffect(() => {
    const attr = (attributes?.autofitColumns || attributes?.autofitcolumns || "");
    if (attr && gridRef && gridRef.current && gridRef.current.api && gridFirstDataRenderred) {
      if (attr.startsWith("{") && attr.endsWith("}")) {
        const jsn = rz.objFromJSON((rz.strReplaceAll(attr, "'", "\"")).trim());
        if (jsn) {
          const okeys = rz.objKeys(jsn);
          if (jsn.columnLimits) { gridRef!.current!.api.sizeColumnsToFit(jsn) }
          else if (okeys[0] && rz.isArray(jsn[okeys[0]])) {
            gridRef!.current!.api.sizeColumnsToFit({defaultMinWidth: 100, columnLimits: jsn[okeys[0]]})
          }
        }
      }
      else if (attr && rootNamespace && rootNamespace[attr]) {
        const clientFNC = rootNamespace[attr];
        if (rz.isPromise(clientFNC)) {
          clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef?.current, lng, dct, safe}).then((ret) => {
            if (ret && rz.isArray(ret)) { gridRef!.current!.api.sizeColumnsToFit(ret) }
          }).catch((err) => { console.log(err) });
        }
        else if (rz.isFunction(clientFNC)) {
          const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, gridRef: gridRef!.current, lng, dct, safe});
          if (ret && rz.isArray(ret)) { gridRef!.current!.api.sizeColumnsToFit(ret) }
        }
      }
      else {
        const awClmns = attr.trim().replace((/(\s+|_|\|)/igm), " ").split(" ").map((cc) => ({ key: cc, minWidth: 100 }));
        if (awClmns.length) { gridRef!.current!.api.sizeColumnsToFit({columnLimits: awClmns, defaultMinWidth: 100}) }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributes, key, rootNamespace, user?.login, lng, gridFirstDataRenderred, elState?.specialParams?.autofitColumns, rootAttributes.key, safe]);

  // ++++++++++++++++++++++++++++++++++++++++++++++++++++
  // ====================================================
  // Global Grid-API to use it inside the clients-script:
  // ====================================================
  const changeState = (params) => {
    if (params && params.fullPath && params.newState) {
      dispatch(changeAppState({appkey: params.appkey || rootAttributes.key, fullPath: params.fullPath, newState: params.newState}));
    }
  };
  const modifyGridRows = (params) => new Promise((resolve) => {
    if (!params || !params.appkey || (!params.gridPath && !params.fullPath && !params.key) || (!params.newRows && !params.editRows && !params.deleteRows && !params.rows)) { return resolve(null) }
    // @ts-ignore
    window.getGridAPI({appkey: params.appkey, fullPath: params.gridPath || params.fullPath || params.key}).then((gridAPI) => {
      if (!gridAPI) { return resolve(null) }

      const serverSideDatasource = gridAPI.getGridOption("serverSideDatasource");
      const weAreInServerSideMode = Boolean(serverSideDatasource && serverSideDatasource.getRows);

      if (params.reload) {
        if (weAreInServerSideMode || params?.rowModelType === "serverSide") {
          gridAPI.refreshServerSide({purge: true, route: (params.route ? params.route : undefined)}); // Reload all rows force
        }
        else {
          // @ts-ignore
          dispatch(changeAppState({appkey: params.appkey, fullPath, newState: {loading: true}}));
        }
      }
      else if (params.rows && rz.isArray(params.rows)) { // Replace all rows
        const mrows = createRowsID(params.rows);
        // Apply changes to AGgrid:
        // 2025-04-05: SSRM
        if (weAreInServerSideMode || params?.rowModelType === "serverSide") {
          gridAPI.applyServerSideRowData({successParams: {rowData: mrows, rowCount: mrows.length + 1}}); // mrows.length + 1 - need to load additional rows when scrolling
        }
        else {
          gridAPI.setGridOption("rowData", mrows);
        }
        // Apply changes to state:
        // @ts-ignore
        dispatch(changeAppState({appkey: params.appkey, fullPath, newState: {data: mrows, loading: false, value: null}}));
        resolve(null);
      }
      else {
        const urows = (params.editRows || []), drows = (params.deleteRows || []), nrows = createRowsID(params.newRows || []);
        const allGridNodes:Array<any> = []; gridAPI.forEachNode((nn) => { allGridNodes.push(nn) });
        const allGridRows = allGridNodes.filter((nn) => (nn.data)).map((nn) => (nn.data));
        const update = urows.map((rr) => (allGridRows.find((nn) => (nn._id === rr._id)) && rr)).filter((rr) => (rr && rr._id));
        const remove = drows.map((rr) => (allGridRows.find((nn) => (nn._id === rr._id)) && rr)).filter((rr) => (rr && rr._id));
        const add = nrows.map((rr) => (!allGridRows.find((nn) => (nn._id === rr._id)) && rr)).filter((rr) => (rr && rr._id));
        // console.log("modifyGridRows()", {appkey: params.appkey, gridPath: params.gridPath || params.fullPath, update, remove, add, insBeforeIndex: params?.insBeforeIndex ?? 0});
        if (update.length || remove.length || add.length) {
          const uObj:{update?:any[], remove?:any[], add?:any[], addIndex?:number, route?:Array<string>} = {};
          if (update.length) { uObj.update = update }
          if (remove.length) { uObj.remove = remove }
          if (add.length) { uObj.add = add; uObj.addIndex = (params?.insBeforeIndex ?? 0) }
          if (params.route && rz.isArray(params.route)) { uObj.route = params.route }

          // Эта хрень работать нормально не захотела! AGgrid входил периодически в бесконечные обновления, что проявлялось "морганием" некоторых строк или "пустыми" строками
          // This crap did not want to work properly! AGgrid showed endless updates, which was expressed by the "blinking" of some lines or it were "empty" lines
          // gridAPI.applyTransactionAsync(
          //   uObj, // update, remove, add, addIndex
          //   () => {
          //     dispatch(modifyAGgridRows({newRows: add, editRows: update, deleteRows: remove, insBeforeIndex: params?.insBeforeIndex ?? 0, appkey: params.appkey, gridPath: params.gridPath || params.fullPath}));
          //     resolve(null); // {newRows: add, editRows: update, deleteRows: remove, insBeforeIndex: params?.insBeforeIndex ?? 0, appkey: params.appkey, gridPath: params.gridPath || params.fullPath}
          //   }
          // );
          // Поэтому я остановился на синхронных транзакциях. Они работают как задумано автором
          // Therefore, I settled on synchronous transactions. They work as planned by the author
          // console.log({uObj, weAreInServerSideMode});
          if (weAreInServerSideMode || params?.rowModelType === "serverSide") {
            gridAPI.applyServerSideTransaction(uObj);
          }
          else {
            gridAPI.applyTransaction(uObj);
          }
          dispatch(modifyAGgridRows({newRows: add, editRows: update, deleteRows: remove, insBeforeIndex: params?.insBeforeIndex ?? 0, appkey: params.appkey, gridPath: params.gridPath || params.fullPath}));
          resolve(null);
        }
        else {
          resolve(null);
        }
      }
    });
  });
  // ++++++++++++++++++++++++++++++++++++++++++++++++++++
  useEffect(() => {
    // @ts-ignore
    if (!window.changeState) { window.changeState = changeState }
    if (!window[rootAttributes.key]) { return }
    // @ts-ignore
    if (!window.modifyGridRows) { window.modifyGridRows = modifyGridRows }

    window[rootAttributes.key][fullPath] = {
      getGridAPI: () => new Promise((resolve) => {
        let gridAPI, counter = 0;
        const timer = setInterval((gr) => {
          // @ts-ignore
          counter++; if ((gr && gr.api && gr.api.getColumnFilterInstance && gr.api.getRowNode) || counter > 20) { gridAPI = gr && gr.api; clearInterval(timer); resolve(gridAPI) }
        }, 100, gridRef.current);
        // gr.api.getColumnState
      }),
      getGridRef: () => new Promise((resolve) => {
        let gridRef1, counter = 0;
        const timer = setInterval((gr) => {
          // @ts-ignore
          counter++; if ((gr && gr.api && gr.api.getColumnFilterInstance && gr.api.getRowNode) || counter > 20) { gridRef1 = gr; clearInterval(timer); resolve(gridRef1) }
        }, 100, gridRef.current);
        // gr.api.getColumnState
      }),
      getSelectedNodes: () => new Promise((resolve) => {
        window[rootAttributes.key][fullPath].getGridAPI().then((gridAPI) => {
          if (!gridAPI) { return resolve({selectedNodes: [], checkedNodes: [], cellRanges: null, gridAPI, startRow: -1, endRow: -1}) }
          const cellRanges = gridAPI?.getCellRanges(); let startRow = -1, endRow = -1;
          const checkedNodes = gridAPI?.getSelectedNodes();
          if (!cellRanges || cellRanges.length !== 1 || !cellRanges[0].startRow) { return resolve({selectedNodes: [], checkedNodes, cellRanges, gridAPI, startRow, endRow}) }
          startRow = Math.min(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex); endRow = Math.max(cellRanges[0].startRow.rowIndex, cellRanges[0].endRow.rowIndex);
          const selectedNodes:Array<any> = [];
          const serverSideDatasource = gridAPI.getGridOption("serverSideDatasource");
          if (serverSideDatasource) {
            gridAPI.forEachNode((node) => {
              if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow) { selectedNodes.push(node) }
            });
          }
          else {
            gridAPI.forEachNodeAfterFilterAndSort((node) => {
              if (!node.group && node.rowIndex >= startRow && node.rowIndex <= endRow) { selectedNodes.push(node) }
            });
          }
          resolve({selectedNodes, checkedNodes, cellRanges, gridAPI, startRow, endRow});
        });
      })
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootAttributes.key, fullPath]);
  // ++++++++++++++++++++++++++++++++++++++++++++++++++++

  // ====================================================
  // Change LNG -> call User function to translate AGgrid content
  // ====================================================
  useEffect(() => {
    if (renderState.lastLNG[key] === lng) { return }
    // @ts-ignore
    renderState.lastLNG[key] = lng;
    const attr = (attributes?.onChangeLNG || attributes?.onchangelng || "");
    if (rootNamespace && rootNamespace[attr]) {
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, data: elState.data, gridRef: gridRef?.current, lng, dct, safe}).then((ret) => {
          if (ret?.err) { console.log(ret.err) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        }).catch((err) => { console.log(err) });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, data: elState.data, gridRef: gridRef?.current, lng, dct, safe});
        if (ret?.err) { console.log(ret.err) }
        if (ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [dct, lng, user, key, attributes, dispatch, elState.data, rootAttributes.key, rootNamespace, safe]);

  // gridApi.startEditingCell({ rowIndex: 0, colKey: "country" });
  // gridApi.stopEditing();
  // const cellDefs = gridApi.getEditingCells(); cellDefs.forEach(cellDef => { console.log(cellDef.rowIndex); console.log(cellDef.column.getId()); console.log(cellDef.floating); });
  // const rowNode = gridApi.getRowNode(this.context.mockEditingId);
  // gridApi.applyTransaction({ update: [updatedToDo] });
  // const onBtStopEditing = useCallback(() => { gridRef.current!.api.stopEditing() }, []);
  // const onBtStartEditing = useCallback(() => { gridRef.current!.api.setFocusedCell(1, 'make'); gridRef.current!.api.startEditingCell({rowIndex: 1, colKey: 'make'}) }, []);

  // gridRef.current.api.applyTransactionAsync({ update: [newItem] }, resultCallback);

  // gridRef.current.api.copySelectedRowsToClipboard();
  // gridRef.current.api.copySelectedRangeToClipboard();

  // ServerSide Row Model Examples:
  // onGridReady(params) {
  //  const initialData = fakeServer.getData({startRow: 0, endRow: 100}); // Maybe a Promise
  //  params.api.applyServerSideRowData({
  //    successParams: {rowData: initialData.rows, rowCount: initialData.lastRow}
  //  });
  // }

  // Debug:
  // useEffect(() => { console.log({agType}) }, [agType]);

  // console.log(elState.data);
  return (
    <div id={key} key={key} className={clsu.join(" ")} style={styl}
      onDragOver={gridDragOver} onDrop={gridDrop}
    >
      <AgGridReact
        ref={gridRef}
        // modules={[
        //   AllEnterpriseModule,
        //   IntegratedChartsModule.with(AgChartsEnterpriseModule)
        // ]}
        // rowData={elState.data} // Replaced by params.api.setGridOption("rowData", globalRowData)
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowClassRules={rowClassRules}
        autoGroupColumnDef={autoGroupColumnDef}
        rowGroupPanelShow={agType.rowGroupPanelShow}
        groupDisplayType={agType.groupDisplayType}
        getChildCount={getChildCount}
        rowModelType={agType.rowModelType}
        rowBuffer={agType.rowBuffer}
        rowSelection={rowSelection}
        cellSelection={agType.cellSelection}
        tooltipShowDelay={agType.tooltipShowDelay}
        maintainColumnOrder={agType.maintainColumnOrder}
        animateRows={agType.animateRows}
        onRowDataUpdated={onRowDataUpdated}
        onGridReady={onGridReady}
        onFirstDataRendered={onFirstDataRendered}
        getContextMenuItems={getContextMenuItems}
        onSortChanged={onSortChanged}
        onFilterChanged={onFilterChanged}
        onColumnRowGroupChanged={onColumnRowGroupChanged}
        onDisplayedColumnsChanged={onDisplayedColumnsChanged}
        onCellSelectionChanged={onCellSelectionChanged}
        onRowSelected={onRowSelected}
        statusBar={statusBar}
        sideBar={sideBar}
        cacheQuickFilter={agType.cacheQuickFilter}
        enableAdvancedFilter={agType.enableAdvancedFilter}
        includeHiddenColumnsInQuickFilter={false}
        treeData={agType.treeData}
        getDataPath={agType.treeData ? getDataPath : undefined}
        groupDefaultExpanded={agType.groupDefaultExpanded}
        alwaysShowHorizontalScroll={true}
        alwaysShowVerticalScroll={true}
        scrollbarWidth={8}
        localeText={localeText}
        masterDetail={agType.masterDetail} // Вложенные таблицы
        rowHeight={agType.rowHeight}
        getRowHeight={getRowHeight}
        detailRowHeight={agType.detailRowHeight}
        detailRowAutoHeight={agType.detailRowAutoHeight}
        detailCellRendererParams={detailCellRendererParams}
        getRowId={getRowId}
        excelStyles={excelStyles}
        defaultExcelExportParams={defaultExcelExportParams}
        enableCharts={agType.enableCharts}
        chartToolPanelsDef={chartToolPanelsDef}
        chartThemeOverrides={chartThemeOverrides}
        editType={agType.editType}
        readOnlyEdit={agType.readOnlyEdit}
        onCellValueChanged={onCellValueChanged}
        onCellEditingStarted={onCellEditingStarted}
        onCellEditingStopped={onCellEditingStopped}
        onRowEditingStopped={onRowEditingStopped}
        onCellEditRequest={onCellEditRequest} // Works only if columnDef: {readOnlyEdit: true}
        // suppressClickEdit={false}
        rowDragManaged={agType.rowDragManaged}
        stopEditingWhenCellsLoseFocus={agType.stopEditingWhenCellsLoseFocus}
        suppressMoveWhenRowDragging={agType.suppressMoveWhenRowDragging}
        rowDragEntireRow={agType.rowDragEntireRow}
        rowDragMultiRow={agType.rowDragMultiRow} // Do not forget set rowSelection='multiple'
        suppressRowDrag={agType.suppressRowDrag}
        onRowDragMove={onRowDragMove}
        onRowDragEnter={onRowDragEnter}
        onRowDragEnd={onRowDragEnd}
        onRowDragLeave={onRowDragLeave}
        // Keyboard events
        onCellKeyDown={onCellKeyDown}
        sendToClipboard={attributes?.sendToClipboard ? sendToClipboard : undefined}

        pivotMode={agType.pivotMode}
        suppressAggFuncInHeader={agType.suppressAggFuncInHeader}

        // Server Side Row Model (SSRM)
        cacheBlockSize={agType.rowModelType === "serverSide" ? (attributes?.cacheBlockSize ? Number(attributes.cacheBlockSize) : 1000) : undefined}
        cacheOverflowSize={(attributes?.cacheOverflowSize ? Number(attributes.cacheOverflowSize) : undefined)}
        blockLoadDebounceMillis={agType.rowModelType === "serverSide" ? (attributes?.blockLoadDebounceMillis ? Number(attributes.blockLoadDebounceMillis) : 1000) : undefined}
        maxBlocksInCache={agType.rowModelType === "serverSide" ? (attributes?.maxBlocksInCache ? Number(attributes.maxBlocksInCache) : 5) : undefined}
        serverSideEnableClientSideSort={agType.rowModelType === "serverSide" ? Boolean(attributes?.serverSideEnableClientSideSort === "true") : undefined}
        serverSideSortAllLevels={agType.rowModelType === "serverSide" ? Boolean(attributes?.serverSideSortAllLevels === "true") : undefined}
        serverSidePivotResultFieldSeparator={agType.rowModelType === "serverSide" && attributes?.serverSidePivotResultFieldSeparator ? attributes?.serverSidePivotResultFieldSeparator : "_"}

        components={customComponents}
      />
    </div>
  );
};

export default AGgrid;

/* eslint-enable @typescript-eslint/no-unused-vars */
